/**
 * Schlanker HTML-Editor für die Signatur (nur Editor, keine Preview/Raw).
 * Toolbar: Font, Größe, Farbe, Hintergrund, Bold/Italic/Underline,
 * Links/Zentriert/Rechts, Rückgängig/Wiederherstellen, Link, Trennlinie,
 * Bild (Dialog: URL oder Datei→Base64), Profilfelder. Bilder: img src http(s) oder data:image.
 * HTML wird mit DOMPurify sanitized (Bild-URL zusätzlich per isAllowedImageUrl geprüft).
 */

import {
  isAllowedLinkHref,
  sanitizeSignatureHtml,
  sanitizeSignatureHtmlResult,
} from "../utils/signatureHtmlSanitizer";
import {
  cssColorToSwatchValue,
  hexToRgb,
  isValidHex,
  normalizeHex,
  parseCssColor,
  rgbToHex,
} from "../utils/cssColor";
import {
  clearScopePreview,
  deleteTable,
  deleteTableColumn,
  deleteTableRow,
  findTableContext,
  getTableCellSelection,
  isMergedTableCell,
  mergeTableCells,
  previewScopeCells,
  insertTableAtCaret,
  insertTableColumn,
  insertTableRow,
  openTableFormatDialog,
  openTableInsertDialog,
  placeCaretInCell,
  splitTableCell,
  type TableCellSelection,
  type TableContext,
  type TableEditorDeps,
  type TableScope,
} from "./editor-tables";
import { createSearchableDropdown } from "./editor-searchlist";
import { prepareAssetImagesForDisplay, resolveAssetImagesForDisplay } from "../utils/signatureAssetImages";
import { parseHtmlInert } from "../utils/inertHtml";
import { parseIntegerInput } from "../utils/editorInputs";
import { graphUserFieldOptions } from "../utils/graphUserFields";
import {
  deleteProfileSelection,
  normalizeProfileSelection,
  toggleHideFieldAtCaret,
  toggleHideLineAtCaret,
} from "../utils/profilePlaceholders";
import { runWithApiBusy, type ApiBusyLock } from "../utils/apiBusy";
import { uploadSignatureAsset } from "../helpers/signature-assets-api";

/**
 * Ergänzt bei img-Tags width/height als HTML-Attribute (aus style oder Platzhalter), falls fehlend.
 */
function ensureImageDimensions(html: string): string {
  if (!html || typeof document === "undefined") return html;
  const div = parseHtmlInert(html);
  div.querySelectorAll("img").forEach((img) => {
    const style = img.getAttribute("style") || "";
    if (!img.hasAttribute("width")) {
      const m = style.match(/\bwidth:\s*(\d+)\s*(?:px)?/);
      if (m) img.setAttribute("width", m[1]);
      else img.setAttribute("width", "200");
    }
    if (!img.hasAttribute("height")) {
      const m = style.match(/\bheight:\s*(\d+)\s*(?:px)?/);
      if (m) img.setAttribute("height", m[1]);
    }
  });
  return div.innerHTML;
}

/** Schriftarten wie in Outlook; jeweils mit Ersatzschriften für andere Clients. */
const FONT_OPTIONS = [
  { label: "Standard", value: "" },
  { label: "Aptos", value: "Aptos, Calibri, 'Segoe UI', sans-serif" },
  { label: "Aptos Display", value: "'Aptos Display', Aptos, Calibri, 'Segoe UI', sans-serif" },
  { label: "Aptos Mono", value: "'Aptos Mono', Consolas, 'Courier New', monospace" },
  { label: "Aptos Narrow", value: "'Aptos Narrow', 'Arial Narrow', Arial, sans-serif" },
  { label: "Aptos Serif", value: "'Aptos Serif', Cambria, Georgia, serif" },
  { label: "Arial", value: "Arial, Helvetica, sans-serif" },
  { label: "Arial Black", value: "'Arial Black', Arial, sans-serif" },
  { label: "Calibri", value: "Calibri, sans-serif" },
  { label: "Calibri Light", value: "'Calibri Light', Calibri, 'Segoe UI', sans-serif" },
  { label: "Cambria", value: "Cambria, Georgia, serif" },
  { label: "Candara", value: "Candara, Calibri, sans-serif" },
  { label: "Century Gothic", value: "'Century Gothic', CenturyGothic, AppleGothic, Arial, sans-serif" },
  { label: "Comic Sans MS", value: "'Comic Sans MS', cursive" },
  { label: "Consolas", value: "Consolas, 'Courier New', monospace" },
  { label: "Constantia", value: "Constantia, Georgia, serif" },
  { label: "Corbel", value: "Corbel, Calibri, sans-serif" },
  { label: "Courier New", value: "'Courier New', Courier, monospace" },
  { label: "Franklin Gothic Book", value: "'Franklin Gothic Book', Arial, sans-serif" },
  { label: "Franklin Gothic Demi", value: "'Franklin Gothic Demi', 'Arial Black', Arial, sans-serif" },
  { label: "Franklin Gothic Medium", value: "'Franklin Gothic Medium', Arial, sans-serif" },
  { label: "Garamond", value: "Garamond, 'Times New Roman', serif" },
  { label: "Georgia", value: "Georgia, serif" },
  { label: "Impact", value: "Impact, Charcoal, sans-serif" },
  { label: "Lucida Console", value: "'Lucida Console', Monaco, monospace" },
  { label: "Lucida Handwriting", value: "'Lucida Handwriting', 'Segoe Script', cursive" },
  { label: "Lucida Sans Unicode", value: "'Lucida Sans Unicode', 'Lucida Grande', sans-serif" },
  { label: "Palatino Linotype", value: "'Palatino Linotype', 'Book Antiqua', Palatino, serif" },
  { label: "Segoe Print", value: "'Segoe Print', 'Comic Sans MS', cursive" },
  { label: "Segoe Script", value: "'Segoe Script', 'Comic Sans MS', cursive" },
  { label: "Segoe UI", value: "'Segoe UI', Tahoma, sans-serif" },
  { label: "Sitka Heading", value: "'Sitka Heading', 'Sitka Text', Cambria, serif" },
  { label: "Sitka Text", value: "'Sitka Text', Cambria, serif" },
  { label: "Tahoma", value: "Tahoma, Geneva, sans-serif" },
  { label: "Times", value: "Times, 'Times New Roman', serif" },
  { label: "Times New Roman", value: "'Times New Roman', Times, serif" },
  { label: "Trebuchet MS", value: "'Trebuchet MS', Helvetica, sans-serif" },
  { label: "Tw Cen MT", value: "'Tw Cen MT', 'Century Gothic', Arial, sans-serif" },
  { label: "Verdana", value: "Verdana, Geneva, sans-serif" },
];

const FONT_SIZES = ["10px", "11px", "12px", "14px", "16px", "18px", "20px"];
const LINE_HEIGHTS = [1, 1.15, 1.5, 2];

function parseCustomFontSizePx(raw: string): number | null {
  const digits = raw.replace(/[^\d]/g, "");
  if (!digits) return null;
  const n = parseInt(digits, 10);
  if (!Number.isFinite(n) || n < 1 || n > 200) return null;
  return n;
}

interface FontSizeDropdown {
  element: HTMLElement;
  /** Zeigt die Größe der Cursorposition an, ohne den Editor-Inhalt zu verändern. */
  setDisplayValue: (size: string) => void;
}

interface LineHeightDropdown {
  element: HTMLElement;
  /** Zeigt den Zeilenabstand der Cursorposition an, ohne den Editor-Inhalt zu verändern. */
  setDisplayValue: (value: string) => void;
}

interface FontFamilyDropdown {
  element: HTMLElement;
  /** Zeigt die Schrift der Cursorposition an, ohne den Editor-Inhalt zu verändern. */
  setDisplayValue: (label: string, value: string) => void;
}

/** Schriftgrößen-Dropdown: vordefinierte Größen + Zahlenfeld für Custom (px). */
function createFontSizeDropdown(
  title: string,
  customLabel: string,
  onApply: (size: string) => void
): FontSizeDropdown {
  const wrap = document.createElement("div");
  wrap.className = "sunnysign-editor-fontsize";
  wrap.title = title;

  const trigger = document.createElement("button");
  trigger.type = "button";
  trigger.className = "sunnysign-editor-fontsize__trigger";
  trigger.setAttribute("aria-haspopup", "listbox");
  trigger.setAttribute("aria-expanded", "false");
  trigger.setAttribute("aria-label", title);
  trigger.textContent = FONT_SIZES[2] ?? "12px";

  const panel = document.createElement("div");
  panel.className = "sunnysign-editor-fontsize__panel";
  panel.setAttribute("role", "listbox");
  panel.hidden = true;

  const onDocPointer = (e: Event): void => {
    if (wrap.contains(e.target as Node)) return;
    setOpen(false);
  };

  const setOpen = (open: boolean): void => {
    panel.hidden = !open;
    trigger.setAttribute("aria-expanded", open ? "true" : "false");
    wrap.classList.toggle("is-open", open);
    if (open) {
      document.addEventListener("mousedown", onDocPointer);
      document.addEventListener("touchstart", onDocPointer, { passive: true });
    } else {
      document.removeEventListener("mousedown", onDocPointer);
      document.removeEventListener("touchstart", onDocPointer);
    }
  };

  const optionButtons: HTMLButtonElement[] = [];

  const setDisplayValue = (size: string): void => {
    trigger.textContent = size;
    optionButtons.forEach((opt) => {
      const active = opt.dataset.value === size;
      opt.classList.toggle("is-active", active);
      opt.setAttribute("aria-selected", active ? "true" : "false");
    });
  };

  const applySize = (size: string): void => {
    setDisplayValue(size);
    setOpen(false);
    onApply(size);
  };

  for (const size of FONT_SIZES) {
    const opt = document.createElement("button");
    opt.type = "button";
    opt.className = "sunnysign-editor-fontsize__option";
    opt.setAttribute("role", "option");
    opt.dataset.value = size;
    opt.textContent = size;
    opt.addEventListener("mousedown", (e) => e.preventDefault());
    opt.addEventListener("click", (e) => {
      e.preventDefault();
      e.stopPropagation();
      applySize(size);
    });
    optionButtons.push(opt);
    panel.appendChild(opt);
  }
  setDisplayValue(trigger.textContent ?? "");

  const customRow = document.createElement("div");
  customRow.className = "sunnysign-editor-fontsize__custom";
  const customLabelEl = document.createElement("span");
  customLabelEl.className = "sunnysign-editor-fontsize__custom-label";
  customLabelEl.textContent = customLabel;
  const customInput = document.createElement("input");
  customInput.type = "text";
  customInput.inputMode = "numeric";
  customInput.pattern = "[0-9]*";
  customInput.className = "sunnysign-editor-fontsize__custom-input";
  customInput.setAttribute("aria-label", customLabel);
  customInput.autocomplete = "off";
  const customUnit = document.createElement("span");
  customUnit.className = "sunnysign-editor-fontsize__custom-unit";
  customUnit.textContent = "px";

  const commitCustom = (): void => {
    const n = parseCustomFontSizePx(customInput.value);
    if (n == null) {
      customInput.value = customInput.value.replace(/[^\d]/g, "");
      return;
    }
    customInput.value = String(n);
    applySize(`${n}px`);
  };

  customInput.addEventListener("mousedown", (e) => e.stopPropagation());
  customInput.addEventListener("click", (e) => e.stopPropagation());
  customInput.addEventListener("input", () => {
    const cleaned = customInput.value.replace(/[^\d]/g, "");
    if (customInput.value !== cleaned) customInput.value = cleaned;
  });
  customInput.addEventListener("keydown", (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      commitCustom();
    } else if (e.key === "Escape") {
      e.preventDefault();
      setOpen(false);
    }
  });

  customRow.appendChild(customLabelEl);
  customRow.appendChild(customInput);
  customRow.appendChild(customUnit);
  panel.appendChild(customRow);

  trigger.addEventListener("mousedown", (e) => {
    e.preventDefault();
    saveSelection();
  });
  trigger.addEventListener("click", (e) => {
    e.preventDefault();
    e.stopPropagation();
    const willOpen = !!panel.hidden;
    setOpen(willOpen);
    if (willOpen) {
      customInput.value = "";
      setTimeout(() => customInput.focus(), 0);
    }
  });

  wrap.appendChild(trigger);
  wrap.appendChild(panel);
  return { element: wrap, setDisplayValue };
}

/**
 * Schriftart-Dropdown mit Suchfeld; jeder Eintrag zeigt sich in seiner eigenen Schrift.
 */
function createFontFamilyDropdown(
  title: string,
  searchPlaceholder: string,
  onApply: (value: string) => void
): FontFamilyDropdown {
  const setDisplayValue = (label: string, value: string): void => {
    dropdown.setTriggerLabel(label);
    const key = fontFamilyKey(value);
    dropdown.setActiveOption(
      (optionValue) => optionValue === value || (!!optionValue && !!key && fontFamilyKey(optionValue) === key)
    );
  };

  const dropdown = createSearchableDropdown({
    title,
    searchPlaceholder,
    extraClass: "sunnysign-editor-fontlist",
    triggerLabel: FONT_OPTIONS[0].label,
    options: FONT_OPTIONS.map((option) => ({
      value: option.value,
      label: option.label,
      previewFont: option.value || undefined,
    })),
    onPick: (value) => {
      const picked = FONT_OPTIONS.find((option) => option.value === value);
      setDisplayValue(picked?.label ?? FONT_OPTIONS[0].label, value);
      onApply(value);
    },
    onBeforeOpen: saveSelection,
  });

  setDisplayValue(FONT_OPTIONS[0].label, FONT_OPTIONS[0].value);
  return { element: dropdown.element, setDisplayValue };
}

function formatLineHeight(value: number): string {
  if (Math.abs(value - 1) < 0.001) return "1.0";
  return String(Math.round(value * 100) / 100);
}

function parseCustomLineHeight(raw: string): number | null {
  const normalized = raw.trim().replace(",", ".");
  if (!/^\d+(?:\.\d{1,2})?$/.test(normalized)) return null;
  const value = Number(normalized);
  return Number.isFinite(value) && value >= 0.5 && value <= 5 ? value : null;
}

/** Zeilenabstands-Dropdown: 1.0 / 1.15 / 1.5 / 2.0 plus benutzerdefinierter Faktor. */
function createLineHeightDropdown(
  title: string,
  customLabel: string,
  invalidMessage: string,
  onApply: (value: number) => void
): LineHeightDropdown {
  const wrap = document.createElement("div");
  wrap.className = "sunnysign-editor-fontsize sunnysign-editor-lineheight";
  wrap.title = title;

  const trigger = document.createElement("button");
  trigger.type = "button";
  trigger.className = "sunnysign-editor-fontsize__trigger sunnysign-editor-lineheight__trigger";
  trigger.setAttribute("aria-haspopup", "listbox");
  trigger.setAttribute("aria-expanded", "false");
  trigger.setAttribute("aria-label", title);
  const triggerIcon = document.createElement("span");
  triggerIcon.className = "sunnysign-editor-lineheight__icon";
  triggerIcon.setAttribute("aria-hidden", "true");
  triggerIcon.textContent = "↕";
  const triggerValue = document.createElement("span");
  triggerValue.className = "sunnysign-editor-lineheight__value";
  triggerValue.textContent = formatLineHeight(LINE_HEIGHTS[0]);
  trigger.appendChild(triggerIcon);
  trigger.appendChild(triggerValue);

  const panel = document.createElement("div");
  panel.className = "sunnysign-editor-fontsize__panel";
  panel.setAttribute("role", "listbox");
  panel.hidden = true;

  const onDocumentPointer = (event: Event): void => {
    if (wrap.contains(event.target as Node)) return;
    setOpen(false);
  };

  const setOpen = (open: boolean): void => {
    panel.hidden = !open;
    trigger.setAttribute("aria-expanded", open ? "true" : "false");
    wrap.classList.toggle("is-open", open);
    if (open) {
      document.addEventListener("mousedown", onDocumentPointer);
      document.addEventListener("touchstart", onDocumentPointer, { passive: true });
    } else {
      document.removeEventListener("mousedown", onDocumentPointer);
      document.removeEventListener("touchstart", onDocumentPointer);
    }
  };

  const optionButtons: HTMLButtonElement[] = [];
  const setDisplayValue = (value: string): void => {
    triggerValue.textContent = value;
    optionButtons.forEach((option) => {
      const active = option.dataset.value === value;
      option.classList.toggle("is-active", active);
      option.setAttribute("aria-selected", active ? "true" : "false");
    });
  };

  const applyValue = (value: number): void => {
    setDisplayValue(formatLineHeight(value));
    setOpen(false);
    onApply(value);
  };

  LINE_HEIGHTS.forEach((value) => {
    const display = formatLineHeight(value);
    const option = document.createElement("button");
    option.type = "button";
    option.className = "sunnysign-editor-fontsize__option";
    option.setAttribute("role", "option");
    option.dataset.value = display;
    option.textContent = display;
    option.addEventListener("mousedown", (event) => event.preventDefault());
    option.addEventListener("click", (event) => {
      event.preventDefault();
      event.stopPropagation();
      applyValue(value);
    });
    optionButtons.push(option);
    panel.appendChild(option);
  });
  setDisplayValue(formatLineHeight(LINE_HEIGHTS[0]));

  const customRow = document.createElement("div");
  customRow.className = "sunnysign-editor-fontsize__custom";
  const customLabelElement = document.createElement("span");
  customLabelElement.className = "sunnysign-editor-fontsize__custom-label";
  customLabelElement.textContent = customLabel;
  const customInput = document.createElement("input");
  customInput.type = "text";
  customInput.inputMode = "decimal";
  customInput.className = "sunnysign-editor-fontsize__custom-input sunnysign-editor-lineheight__custom-input";
  customInput.setAttribute("aria-label", customLabel);
  customInput.autocomplete = "off";

  const commitCustom = (): void => {
    const value = parseCustomLineHeight(customInput.value);
    if (value === null) {
      customInput.setCustomValidity(invalidMessage);
      customInput.reportValidity();
      return;
    }
    customInput.setCustomValidity("");
    customInput.value = formatLineHeight(value);
    applyValue(value);
  };

  customInput.addEventListener("mousedown", (event) => event.stopPropagation());
  customInput.addEventListener("click", (event) => event.stopPropagation());
  customInput.addEventListener("input", () => customInput.setCustomValidity(""));
  customInput.addEventListener("keydown", (event) => {
    if (event.key === "Enter") {
      event.preventDefault();
      commitCustom();
    } else if (event.key === "Escape") {
      event.preventDefault();
      setOpen(false);
    }
  });

  customRow.appendChild(customLabelElement);
  customRow.appendChild(customInput);
  panel.appendChild(customRow);

  trigger.addEventListener("mousedown", (event) => {
    event.preventDefault();
    saveSelection();
  });
  trigger.addEventListener("click", (event) => {
    event.preventDefault();
    event.stopPropagation();
    const willOpen = !!panel.hidden;
    setOpen(willOpen);
    if (willOpen) {
      customInput.value = "";
      customInput.setCustomValidity("");
      setTimeout(() => customInput.focus(), 0);
    }
  });

  wrap.appendChild(trigger);
  wrap.appendChild(panel);
  return { element: wrap, setDisplayValue };
}

/** Standard-Profilfelder, falls API/Graph keine liefert (immer komplette Liste). */
const DEFAULT_PROFILE_FIELDS: ProfileFieldOption[] = graphUserFieldOptions();

/** Kompatibel mit ProfileFieldOption aus signature-api (API/Graph). */
type ProfileFieldOption = {
  id: string;
  label: string;
  token: string;
  hasValue?: boolean;
  /** Wert des angemeldeten Benutzers; steht im Tooltip vor der Erklärung. */
  value?: string;
};

/** Tooltip eines Listeneintrags: eigener Wert (oder Hinweis darauf) plus Erklärung. */
function profileFieldOptionHint(t: (key: string) => string, field: ProfileFieldOption): string {
  const value = field.value?.trim() ?? "";
  // Freitextfelder wie aboutMe können sehr lang sein; der Tooltip bleibt lesbar.
  const shortened = value.length > 15 ? `${value.slice(0, 15)}…` : value;
  const prefix = shortened || t("editor.profileFieldNoValue");
  return `${prefix} - ${t("editor.profileFieldItemHint")}`;
}

export type SignatureEditorInitOptions = {
  /** Profilfeld-Dropdown und Platzhalter-Auswahl (Standard: true). */
  showProfileFields?: boolean;
  /** i18n-Key für Editor-Platzhalter (Standard: form.placeholderContent). */
  placeholderKey?: string;
  /** http(s)-URL im Bild-Dialog (Standard: true). */
  allowImageUrl?: boolean;
  /** Datei→Base64 im Bild-Dialog (Standard: false — product-fixed off). */
  allowImageBase64?: boolean;
  /** Datei→Blob + cid Inline-Attachment (Standard: false). */
  allowImageAsAttachment?: boolean;
  /** Erlaubte Attachment-Formate (png, jpeg, gif, webp). */
  allowedAttachmentFormats?: string[];
  /** Fires after content changes (typing, paste, toolbar). */
  onContentChange?: () => void;
};

/** Beschriftung mit Erklärung; der Rahmen trägt den Tooltip, damit er auch bei deaktivierter Schaltfläche erscheint. */
function createHintWrapper(hint: string): HTMLSpanElement {
  const wrap = document.createElement("span");
  wrap.className = "sunnysign-editor-hint-wrap";
  wrap.title = hint;
  return wrap;
}

/** Outlook-Style: Fett = erster Buchstabe (F/B) in Fett */
function iconBold(t: (key: string) => string): string {
  const letter = t("editor.bold").charAt(0).toUpperCase();
  return `<span class="sunnysign-editor-icon-letter sunnysign-editor-icon-bold" aria-hidden="true">${letter}</span>`;
}
/** Outlook-Style: Kursiv = erster Buchstabe (K/I) in Kursiv */
function iconItalic(t: (key: string) => string): string {
  const letter = t("editor.italic").charAt(0).toUpperCase();
  return `<span class="sunnysign-editor-icon-letter sunnysign-editor-icon-italic" aria-hidden="true">${letter}</span>`;
}
/** Outlook-Style: Unterstrichen = U mit Unterstreichung */
function iconUnderline(t: (key: string) => string): string {
  const letter = t("editor.underline").charAt(0).toUpperCase();
  return `<span class="sunnysign-editor-icon-letter sunnysign-editor-icon-underline" aria-hidden="true">${letter}</span>`;
}
function iconAlignLeft(): string {
  return '<svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M15 15H3v2h12v-2zm0-8H3v2h12V7zM3 13h18v-2H3v2zm0 8h18v-2H3v2zM3 3v2h18V3H3z"/></svg>';
}
function iconAlignCenter(): string {
  return '<svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M7 15v2h10v-2H7zm-4 6h18v-2H3v2zm0-8h18v-2H3v2zm4-6v2h10V7H7zM3 3v2h18V3H3z"/></svg>';
}
function iconAlignRight(): string {
  return '<svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M3 21h18v-2H3v2zm6-4h12v-2H9v2zm-6-4h18v-2H3v2zm6-4h12V7H9v2zM3 3v2h18V3H3z"/></svg>';
}
function iconUndo(): string {
  return '<svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M12.5 8c-2.65 0-5.05.99-6.9 2.6L2 7v9h9l-3.62-3.62c1.39-1.16 3.16-1.88 5.12-1.88 3.54 0 6.55 2.31 7.6 5.5l2.37-.78C21.08 11.03 17.15 8 12.5 8z"/></svg>';
}
function iconRedo(): string {
  return '<svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M18.4 10.6C16.55 8.99 14.15 8 11.5 8c-4.65 0-8.58 3.03-9.96 7.22L3.9 16c1.05-3.19 4.05-5.5 7.6-5.5 1.95 0 3.73.72 5.12 1.88L13 16h9V7l-3.6 3.6z"/></svg>';
}
function iconLink(): string {
  return '<svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M3.9 12c0-1.71 1.39-3.1 3.1-3.1h4V7H7c-2.76 0-5 2.24-5 5s2.24 5 5 5h4v-1.9H7c-1.71 0-3.1-1.39-3.1-3.1zM8 13h8v-2H8v2zm9-6h-4v1.9h4c1.71 0 3.1 1.39 3.1 3.1s-1.39 3.1-3.1 3.1h-4V17h4c2.76 0 5-2.24 5-5s-2.24-5-5-5z"/></svg>';
}
function iconHr(): string {
  return '<svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M5 12h14v1H5z"/></svg>';
}
function iconImage(): string {
  return '<svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M21 19V5c0-1.1-.9-2-2-2H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2zM8.5 13.5l2.5 3.01L14.5 12l4.5 6H5l3.5-4.5z"/></svg>';
}
const TABLE_ICON_ATTRS =
  'width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" aria-hidden="true"';
/** Tabellen-Icon: Raster mit Innenlinien. */
function iconTable(): string {
  return `<svg ${TABLE_ICON_ATTRS}><rect x="3" y="4.5" width="18" height="15" rx="1"/><path d="M3 9.5h18M3 14.5h18M9 4.5v15M15 4.5v15"/></svg>`;
}
/** Raster mit Plus in der oberen bzw. unteren Hälfte = Zeile darüber/darunter. */
function iconRowInsert(where: "above" | "below"): string {
  const plus = where === "above" ? '<path d="M12 6v4M10 8h4"/>' : '<path d="M12 14v4M10 16h4"/>';
  return `<svg ${TABLE_ICON_ATTRS}><rect x="3" y="4.5" width="18" height="15" rx="1"/><path d="M3 12h18"/>${plus}</svg>`;
}
/** Raster mit Plus in der linken bzw. rechten Hälfte = Spalte links/rechts. */
function iconColumnInsert(where: "left" | "right"): string {
  const plus = where === "left" ? '<path d="M7 10v4M5 12h4"/>' : '<path d="M17 10v4M15 12h4"/>';
  return `<svg ${TABLE_ICON_ATTRS}><rect x="3" y="4.5" width="18" height="15" rx="1"/><path d="M12 4.5v15"/>${plus}</svg>`;
}
/** Raster mit Balken auf der mittleren Zeile = Zeile entfernen. */
function iconRowDelete(): string {
  return `<svg ${TABLE_ICON_ATTRS}><rect x="3" y="4.5" width="18" height="15" rx="1"/><path d="M3 9.5h18M3 14.5h18"/><path d="M8 12h8"/></svg>`;
}
/** Raster mit Balken auf der mittleren Spalte = Spalte entfernen. */
function iconColumnDelete(): string {
  return `<svg ${TABLE_ICON_ATTRS}><rect x="3" y="4.5" width="18" height="15" rx="1"/><path d="M9 4.5v15M15 4.5v15"/><path d="M12 8v8"/></svg>`;
}
/** Zwei Zellen mit aufgelöster Mittellinie = verbinden; mit Mittellinie = teilen. */
function iconMergeCells(split = false): string {
  const middle = split ? '<path d="M12 4.5v15"/>' : '<path d="M9 12h6M12 9v6"/>';
  return `<svg ${TABLE_ICON_ATTRS}><rect x="3" y="4.5" width="18" height="15" rx="1"/>${middle}</svg>`;
}
/** Papierkorb = ganze Tabelle entfernen. */
function iconTableDelete(): string {
  return `<svg ${TABLE_ICON_ATTRS}><path d="M4 7h16M10 7V5h4v2M6 7l1 13h10l1-13"/></svg>`;
}
function iconProfile(): string {
  return '<svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z"/></svg>';
}
/** Outlook-Style: Schriftfarbe = A mit Farbbalken darunter */
function iconTextColor(): string {
  return '<span class="sunnysign-editor-icon-textcolor" aria-hidden="true"><span class="sunnysign-editor-icon-a">A</span><span class="sunnysign-editor-icon-colorbar"></span></span>';
}
/** Hintergrundfarbe = Marker.svg (Körper + Keilspitze + Linie) + Farbbalken darunter */
function iconBgColor(): string {
  return '<span class="sunnysign-editor-icon-bgcolor" aria-hidden="true"><svg class="sunnysign-editor-icon-highlighter" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><path d="M5 19l2-5L15.5 5.5a2.3 2.3 0 0 1 3.2 0l.3.3a2.3 2.3 0 0 1 0 3.2L10.5 17.5 5 19z"/><path d="M7 14l3 3"/><path d="M3 21h10"/></svg><span class="sunnysign-editor-icon-colorbar"></span></span>';
}

function openImageDialog(
  editorEl: HTMLElement,
  t: (key: string) => string,
  showMessage?: (msg: string) => void,
  imageOpts?: {
    allowImageUrl?: boolean;
    allowImageBase64?: boolean;
    allowImageAsAttachment?: boolean;
    allowedAttachmentFormats?: string[];
  }
): void {
  const allowUrl = imageOpts?.allowImageUrl !== false;
  const allowBase64 = imageOpts?.allowImageBase64 !== false;
  const allowAttachment = imageOpts?.allowImageAsAttachment === true;
  const allowFile = allowBase64 || allowAttachment;
  if (!allowUrl && !allowFile) {
    showMessage?.(t("editor.imageDisabled"));
    return;
  }

  const formats = (imageOpts?.allowedAttachmentFormats?.length
    ? imageOpts.allowedAttachmentFormats
    : ["png", "jpeg", "gif", "webp"]
  ).map((f) => (f === "jpg" ? "jpeg" : f.toLowerCase()));

  const acceptParts: string[] = [];
  for (const f of formats) {
    if (f === "png") acceptParts.push("image/png", ".png");
    if (f === "jpeg") acceptParts.push("image/jpeg", ".jpg", ".jpeg");
    if (f === "gif") acceptParts.push("image/gif", ".gif");
    if (f === "webp") acceptParts.push("image/webp", ".webp");
  }

  const overlay = document.createElement("div");
  overlay.className = "sunnysign-editor-dialog-overlay";
  const box = document.createElement("div");
  box.className = "sunnysign-editor-dialog-box";

  const urlLabel = document.createElement("label");
  urlLabel.textContent = t("editor.imageUrlPrompt");
  urlLabel.setAttribute("for", "sunnysign-image-url-input");
  const input = document.createElement("input");
  input.type = "url";
  input.id = "sunnysign-image-url-input";
  input.className = "sunnysign-input";
  input.placeholder = "https://…";

  const fileLabel = document.createElement("label");
  fileLabel.textContent = allowAttachment
    ? t("editor.imageFileAttachmentPrompt")
    : allowUrl
      ? t("editor.imageFilePrompt")
      : t("editor.imageFilePromptOnly");
  fileLabel.setAttribute("for", "sunnysign-image-file-input");
  const fileInput = document.createElement("input");
  fileInput.type = "file";
  fileInput.id = "sunnysign-image-file-input";
  fileInput.className = "sunnysign-input";
  fileInput.accept = acceptParts.length ? acceptParts.join(",") : "image/png,image/jpeg,image/gif,.png,.jpg,.jpeg,.gif";
  const fileHint = document.createElement("p");
  fileHint.className = "sunnysign-hint";
  fileHint.textContent = allowAttachment ? t("editor.imageFileAttachmentHint") : t("editor.imageFileHint");

  const widthLabel = document.createElement("label");
  widthLabel.textContent = t("editor.imageWidth");
  widthLabel.setAttribute("for", "sunnysign-image-width");
  const widthInput = document.createElement("input");
  widthInput.type = "text";
  widthInput.id = "sunnysign-image-width";
  widthInput.className = "sunnysign-input";
  widthInput.placeholder = "z. B. 100";
  const widthUnitSelect = document.createElement("select");
  widthUnitSelect.className = "sunnysign-editor-select sunnysign-image-width-unit";
  widthUnitSelect.setAttribute("aria-label", t("editor.imageWidthUnit"));
  const widthUnitPx = document.createElement("option");
  widthUnitPx.value = "px";
  widthUnitPx.textContent = "px";
  const widthUnitPercent = document.createElement("option");
  widthUnitPercent.value = "%";
  widthUnitPercent.textContent = "%";
  widthUnitSelect.appendChild(widthUnitPx);
  widthUnitSelect.appendChild(widthUnitPercent);
  const widthRow = document.createElement("div");
  widthRow.className = "sunnysign-image-size-row";
  widthRow.appendChild(widthInput);
  widthRow.appendChild(widthUnitSelect);
  const heightLabel = document.createElement("label");
  heightLabel.textContent = t("editor.imageHeight");
  heightLabel.setAttribute("for", "sunnysign-image-height");
  const heightInput = document.createElement("input");
  heightInput.type = "text";
  heightInput.id = "sunnysign-image-height";
  heightInput.className = "sunnysign-input";
  heightInput.placeholder = "z. B. 100";
  heightInput.value = "auto";
  const heightUnitSelect = document.createElement("select");
  heightUnitSelect.className = "sunnysign-editor-select sunnysign-image-height-unit";
  heightUnitSelect.setAttribute("aria-label", t("editor.imageHeightUnit"));
  const heightUnitAuto = document.createElement("option");
  heightUnitAuto.value = "auto";
  heightUnitAuto.textContent = "auto";
  const heightUnitPx = document.createElement("option");
  heightUnitPx.value = "px";
  heightUnitPx.textContent = "px";
  const heightUnitPercent = document.createElement("option");
  heightUnitPercent.value = "%";
  heightUnitPercent.textContent = "%";
  heightUnitSelect.appendChild(heightUnitAuto);
  heightUnitSelect.appendChild(heightUnitPx);
  heightUnitSelect.appendChild(heightUnitPercent);
  heightUnitSelect.value = "auto";
  const heightRow = document.createElement("div");
  heightRow.className = "sunnysign-image-size-row";
  heightRow.appendChild(heightInput);
  heightRow.appendChild(heightUnitSelect);
  heightInput.disabled = true;

  function syncHeightFromUnit(): void {
    if (heightUnitSelect.value === "auto") {
      heightInput.disabled = true;
      heightInput.value = "auto";
    } else {
      heightInput.disabled = false;
      if (heightInput.value.trim().toLowerCase() === "auto") heightInput.value = "";
      heightInput.placeholder = heightUnitSelect.value === "%" ? "z. B. 50" : "z. B. 100";
    }
  }
  function syncUnitFromInput(): void {
    if (heightInput.value.trim().toLowerCase() === "auto") {
      heightUnitSelect.value = "auto";
      heightInput.disabled = true;
      heightInput.value = "auto";
    }
  }
  heightUnitSelect.addEventListener("change", syncHeightFromUnit);
  heightInput.addEventListener("input", syncUnitFromInput);
  heightInput.addEventListener("blur", syncUnitFromInput);
  // Die Einheit steht im Auswahlfeld; im Zahlenfeld sind nur Ziffern sinnvoll.
  [widthInput, heightInput].forEach((field) => {
    field.addEventListener("input", () => {
      const cleaned = field.value.replace(/[^\d]/g, "");
      if (field.value !== cleaned && field.value.trim().toLowerCase() !== "auto") field.value = cleaned;
    });
  });

  /** Erster Fehler in den Maßangaben, sonst null. */
  function findSizeProblem(): { key: string; field: HTMLInputElement } | null {
    const maxFor = (unit: string): number => (unit === "%" ? 100 : 5000);
    const width = widthInput.value.trim();
    if (width && parseIntegerInput(width, 1, maxFor(widthUnitSelect.value)) === null) {
      return { key: "editor.imageWidthInvalid", field: widthInput };
    }
    const height = heightInput.value.trim();
    if (
      heightUnitSelect.value !== "auto" &&
      height &&
      parseIntegerInput(height, 1, maxFor(heightUnitSelect.value)) === null
    ) {
      return { key: "editor.imageHeightInvalid", field: heightInput };
    }
    return null;
  }

  const alignLabel = document.createElement("label");
  alignLabel.textContent = t("editor.imageAlign");
  alignLabel.setAttribute("for", "sunnysign-image-align");
  const alignSelect = document.createElement("select");
  alignSelect.id = "sunnysign-image-align";
  alignSelect.className = "sunnysign-editor-select sunnysign-image-align";
  [
    { value: "", label: t("editor.imageAlignDefault") },
    { value: "left", label: t("editor.alignLeft") },
    { value: "center", label: t("editor.alignCenter") },
    { value: "right", label: t("editor.alignRight") },
  ].forEach((o) => {
    const opt = document.createElement("option");
    opt.value = o.value;
    opt.textContent = o.label;
    alignSelect.appendChild(opt);
  });

  const dialogError = document.createElement("p");
  dialogError.className = "sunnysign-editor-dialog-error";
  dialogError.setAttribute("role", "alert");
  dialogError.hidden = true;

  function showDialogError(msg: string): void {
    dialogError.textContent = msg;
    dialogError.hidden = false;
  }

  function clearDialogError(): void {
    dialogError.textContent = "";
    dialogError.hidden = true;
  }

  function buildImgTag(
    src: string,
    asset?: { assetId: string; fileName: string; contentType: string }
  ): string {
    const w = widthInput.value.trim();
    const widthUnit = widthUnitSelect.value;
    const heightUnit = heightUnitSelect.value;
    const h = heightInput.value.trim();
    const styleParts: string[] = ["max-width:100%"];
    const numW = w ? w.replace(/\s*(px|%)\s*$/gi, "").trim() : "";
    const numH = h && heightUnit !== "auto" ? h.replace(/\s*(px|%)\s*$/gi, "").trim() : "";
    if (w) {
      const widthValue = widthUnit === "%" ? `${numW}%` : `${numW}px`;
      styleParts.push(`width:${widthValue}`);
    }
    if (heightUnit === "auto") styleParts.push("height:auto");
    else if (numH) styleParts.push(`height:${heightUnit === "%" ? `${numH}%` : `${numH}px`}`);
    else styleParts.push("height:auto");
    const style = styleParts.join(";");
    const safeSrc = src.replace(/"/g, "&quot;");
    // Marker, um das Bild direkt nach dem Einfügen ausrichten zu können; wird sofort entfernt.
    const attrParts: string[] = [
      `src="${safeSrc}"`,
      `alt=""`,
      `style="${style.replace(/"/g, "&quot;")}"`,
      `data-sunnysign-new=""`,
    ];
    if (widthUnit === "px" && numW && /^\d+$/.test(numW)) attrParts.push(`width="${numW}"`);
    if (heightUnit === "px" && numH && /^\d+$/.test(numH)) attrParts.push(`height="${numH}"`);
    if (asset) {
      attrParts.push(`data-sunnysign-asset="${asset.assetId.replace(/"/g, "")}"`);
      attrParts.push(`data-sunnysign-filename="${asset.fileName.replace(/"/g, "")}"`);
      attrParts.push(`data-sunnysign-ctype="${asset.contentType.replace(/"/g, "")}"`);
    }
    return `<img ${attrParts.join(" ")}>`;
  }

  function insertImageSrc(
    src: string,
    asset?: { assetId: string; fileName: string; contentType: string }
  ): void {
    const imgTag = buildImgTag(src, asset);
    close();
    if (editorBody) {
      editorBody.focus();
      // Auswahl vom Öffnen des Dialogs wiederherstellen; nur ohne Auswahl ans Ende anfügen.
      if (!restoreSelection()) placeCaretAtEnd(editorBody);
      insertHtml(imgTag);
      applyInsertedImageAlignment(editorBody, alignSelect.value);
    }
  }

  const actions = document.createElement("div");
  actions.className = "sunnysign-form-actions";
  const ok = document.createElement("button");
  ok.type = "button";
  ok.className = "sunnysign-btn sunnysign-btn--primary";
  ok.textContent = "OK";
  const cancel = document.createElement("button");
  cancel.type = "button";
  cancel.className = "sunnysign-btn sunnysign-btn--secondary";
  cancel.textContent = t("form.cancel");
  const close = () => {
    overlay.remove();
    editorBody?.focus();
  };
  const imageUploadLock: ApiBusyLock = { inFlight: false };
  ok.addEventListener("click", () => {
    clearDialogError();
    if (imageUploadLock.inFlight) return;
    const sizeProblem = findSizeProblem();
    if (sizeProblem) {
      showDialogError(t(sizeProblem.key));
      sizeProblem.field.focus();
      return;
    }
    const file = allowFile ? fileInput.files?.[0] : undefined;
    if (file) {
      if (!isAllowedImageFile(file, formats)) {
        showDialogError(t("editor.imageFileInvalid"));
        return;
      }
      if (allowAttachment) {
        if (file.size > MAX_ATTACHMENT_IMAGE_BYTES) {
          showDialogError(t("editor.imageFileTooLargeAttachment"));
          return;
        }
        void runWithApiBusy({ buttons: [ok, cancel], lock: imageUploadLock }, async () => {
          try {
            const uploaded = await uploadSignatureAsset(file);
            const previewUrl = URL.createObjectURL(file);
            insertImageSrc(previewUrl, {
              assetId: uploaded.assetId,
              fileName: uploaded.fileName,
              contentType: uploaded.contentType,
            });
          } catch (e) {
            console.warn("[SunnySign] Asset-Upload fehlgeschlagen:", e);
            showDialogError(t("editor.imageUploadFailed"));
          }
        });
        return;
      }
      if (file.size > MAX_EMBEDDED_IMAGE_BYTES) {
        showDialogError(t("editor.imageFileTooLarge"));
        return;
      }
      const reader = new FileReader();
      reader.onload = () => {
        const dataUrl = typeof reader.result === "string" ? reader.result : "";
        if (!isAllowedImageUrl(dataUrl)) {
          showDialogError(t("editor.imageFileInvalid"));
          return;
        }
        insertImageSrc(dataUrl);
      };
      reader.onerror = () => showDialogError(t("editor.imageFileInvalid"));
      reader.readAsDataURL(file);
      return;
    }
    if (!allowUrl) {
      showDialogError(t("editor.imageFileRequired"));
      return;
    }
    const url = input.value.trim();
    if (!url) {
      close();
      return;
    }
    if (!isAllowedImageUrl(url)) {
      showDialogError(t("editor.imageUrlInvalid"));
      return;
    }
    insertImageSrc(url);
  });
  cancel.addEventListener("click", close);
  overlay.addEventListener("click", (e) => { if (e.target === overlay) close(); });
  fileInput.addEventListener("change", clearDialogError);
  input.addEventListener("input", clearDialogError);
  widthInput.addEventListener("input", clearDialogError);
  heightInput.addEventListener("input", clearDialogError);
  actions.appendChild(ok);
  actions.appendChild(cancel);
  if (allowUrl) {
    box.appendChild(urlLabel);
    box.appendChild(input);
  }
  if (allowFile) {
    box.appendChild(fileLabel);
    box.appendChild(fileInput);
    box.appendChild(fileHint);
  }
  box.appendChild(widthLabel);
  box.appendChild(widthRow);
  box.appendChild(heightLabel);
  box.appendChild(heightRow);
  box.appendChild(alignLabel);
  box.appendChild(alignSelect);
  box.appendChild(dialogError);
  box.appendChild(actions);
  overlay.appendChild(box);
  document.body.appendChild(overlay);
  if (allowUrl) input.focus();
  else fileInput.focus();
}

/**
 * Richtet das zuletzt eingefügte Bild aus: in einer Tabellenzelle über die Zelle
 * (so machen es E-Mail-Clients zuverlässig), außerhalb über die Ränder des Bildes.
 */
function applyInsertedImageAlignment(editorEl: HTMLElement, align: string): void {
  const img = editorEl.querySelector<HTMLImageElement>("img[data-sunnysign-new]");
  if (!img) return;
  img.removeAttribute("data-sunnysign-new");
  if (!align) return;
  const cell = img.closest("td,th") as HTMLTableCellElement | null;
  if (cell) {
    cell.style.textAlign = align;
    cell.setAttribute("align", align);
    return;
  }
  img.style.display = "block";
  img.style.marginLeft = align === "left" ? "0" : "auto";
  img.style.marginRight = align === "right" ? "0" : "auto";
}

/** Gibt das <a>-Element zurück, das die aktuelle Auswahl enthält (oder in dem die Auswahl liegt). */
function getLinkInSelection(): HTMLAnchorElement | null {
  const sel = window.getSelection();
  if (!sel || !sel.rangeCount || !editorBody) return null;
  let node: Node | null = sel.getRangeAt(0).commonAncestorContainer;
  if (node.nodeType === Node.TEXT_NODE && node.parentElement) node = node.parentElement;
  if (!node || node.nodeType !== Node.ELEMENT_NODE) return null;
  const a = (node as Element).closest("a");
  return editorBody.contains(a) ? a : null;
}

const UNSTYLED_LINK_FALLBACK_COLOR = "#000000";

/** Explizit gesetzte Schriftfarbe eines Elements (Inline-Style oder Legacy <font color>). */
function explicitColorOf(el: Element): string | null {
  const inline = (el as HTMLElement).style?.color?.trim();
  if (inline && inline.toLowerCase() !== "inherit") return inline;
  if (el.tagName === "FONT") {
    const attr = el.getAttribute("color")?.trim();
    if (attr) return attr;
  }
  return null;
}

/** Nächste explizit gesetzte Schriftfarbe oberhalb des Links im Editor. */
function inheritedTextColor(start: Element | null): string | null {
  let el: Element | null = start;
  while (el && editorBody && el !== editorBody && editorBody.contains(el)) {
    const color = explicitColorOf(el);
    if (color) return color;
    el = el.parentElement;
  }
  return null;
}

/** Erste explizit gesetzte Schriftfarbe innerhalb des Links (z. B. vorher eingefärbter Text). */
function contentTextColor(anchor: HTMLAnchorElement): string | null {
  for (const el of Array.from(anchor.querySelectorAll("*"))) {
    const color = explicitColorOf(el);
    if (color) return color;
  }
  return null;
}

/**
 * Entfernt den Standard-Link-Stil (blau, unterstrichen). Outlook färbt <a> über die Hyperlink-Formatvorlage,
 * `color: inherit` greift dort nicht — daher konkrete Farbe und zusätzlich ein innerer <span>, der die Vorlage überstimmt.
 */
function clearLinkStyle(anchor: HTMLAnchorElement): void {
  const innerColor = contentTextColor(anchor);
  const color = innerColor ?? inheritedTextColor(anchor.parentElement) ?? UNSTYLED_LINK_FALLBACK_COLOR;
  anchor.style.textDecoration = "none";
  anchor.style.color = color;
  if (innerColor || !anchor.firstChild) return;
  const wrap = document.createElement("span");
  wrap.style.color = color;
  while (anchor.firstChild) wrap.appendChild(anchor.firstChild);
  anchor.appendChild(wrap);
}

/**
 * Link-Schemes des Dialogs. Die Reihenfolge entscheidet, welches Präfix in einer
 * eingetippten oder eingefügten URL erkannt wird – „https://“ deshalb vor „http://“.
 */
const LINK_SCHEMES: { value: string; placeholderKey: string }[] = [
  { value: "https://", placeholderKey: "editor.linkTargetWeb" },
  { value: "http://", placeholderKey: "editor.linkTargetWeb" },
  { value: "mailto:", placeholderKey: "editor.linkTargetMail" },
  { value: "tel:", placeholderKey: "editor.linkTargetPhone" },
];

/** Trennt eine URL in Scheme und Rest; ohne bekanntes Scheme gilt https://. */
function splitLinkHref(href: string): { scheme: string; rest: string } {
  const trimmed = href.trim();
  const lower = trimmed.toLowerCase();
  const match = LINK_SCHEMES.find((scheme) => lower.startsWith(scheme.value));
  if (!match) return { scheme: LINK_SCHEMES[0].value, rest: trimmed };
  return { scheme: match.value, rest: trimmed.slice(match.value.length) };
}

/** Link an der Cursorposition dieses Bereichs. */
function anchorInRange(range: Range | null): HTMLAnchorElement | null {
  if (!range || !editorBody) return null;
  let node: Node | null = range.commonAncestorContainer;
  if (node.nodeType === Node.TEXT_NODE) node = node.parentElement;
  if (!node || node.nodeType !== Node.ELEMENT_NODE) return null;
  const anchor = (node as Element).closest("a");
  return anchor && editorBody.contains(anchor) ? (anchor as HTMLAnchorElement) : null;
}

/** Neuer Link an der Cursorposition, wenn nichts markiert ist (createLink braucht eine Markierung). */
function insertLinkAtCaret(href: string, text: string): HTMLAnchorElement | null {
  const sel = window.getSelection();
  if (!sel || sel.rangeCount === 0) return null;
  const range = sel.getRangeAt(0);
  if (editorBody && !editorBody.contains(range.commonAncestorContainer)) return null;
  const anchor = document.createElement("a");
  anchor.setAttribute("href", href);
  anchor.textContent = text;
  range.deleteContents();
  range.insertNode(anchor);
  const after = document.createRange();
  after.setStartAfter(anchor);
  after.collapse(true);
  sel.removeAllRanges();
  sel.addRange(after);
  editorBody?.dispatchEvent(new Event("input", { bubbles: true }));
  return anchor;
}

/**
 * Link-Dialog: Scheme-Auswahl (https, http, mailto, tel), Ziel, optional ein Profilfeld
 * im Ziel (z. B. „mailto:“ + {{mail}}) und die Checkbox „Kein Stil“. Steht der Cursor in
 * einem Link, sind dessen Werte vorbelegt.
 */
function openLinkDialog(
  t: (key: string) => string,
  showMessage?: (msg: string) => void,
  profileFields: ProfileFieldOption[] = []
): void {
  // Eigene Kopie der Auswahl: Dropdowns im Dialog überschreiben die globale.
  const selection = window.getSelection();
  const editorRange =
    selection && selection.rangeCount && editorBody?.contains(selection.anchorNode)
      ? selection.getRangeAt(0).cloneRange()
      : savedSelectionRange?.cloneRange() ?? null;
  const existingAnchor = anchorInRange(editorRange);
  const initial = splitLinkHref(existingAnchor?.getAttribute("href") ?? "");

  const overlay = document.createElement("div");
  overlay.className = "sunnysign-editor-dialog-overlay sunnysign-editor-link-dialog";
  overlay.setAttribute("role", "dialog");
  overlay.setAttribute("aria-modal", "true");
  const box = document.createElement("div");
  box.className = "sunnysign-editor-dialog-box sunnysign-editor-link-dialog__box";

  const title = document.createElement("h3");
  title.className = "sunnysign-editor-link-dialog__title";
  title.textContent = t("editor.linkTitle");

  const grid = document.createElement("div");
  grid.className = "sunnysign-editor-link-dialog__grid";

  const schemeField = document.createElement("div");
  schemeField.className = "sunnysign-editor-link-dialog__field sunnysign-editor-link-dialog__field--scheme";
  const schemeLabel = document.createElement("label");
  schemeLabel.className = "sunnysign-editor-link-dialog__label";
  schemeLabel.htmlFor = "sunnysign-link-scheme";
  schemeLabel.textContent = t("editor.linkScheme");
  const schemeSelect = document.createElement("select");
  schemeSelect.id = "sunnysign-link-scheme";
  schemeSelect.className = "sunnysign-input sunnysign-editor-link-dialog__scheme";
  LINK_SCHEMES.forEach((scheme) => {
    const option = document.createElement("option");
    option.value = scheme.value;
    option.textContent = scheme.value;
    schemeSelect.appendChild(option);
  });
  schemeSelect.value = initial.scheme;
  schemeField.appendChild(schemeLabel);
  schemeField.appendChild(schemeSelect);

  const targetField = document.createElement("div");
  targetField.className = "sunnysign-editor-link-dialog__field sunnysign-editor-link-dialog__field--target";
  const targetLabel = document.createElement("label");
  targetLabel.className = "sunnysign-editor-link-dialog__label";
  targetLabel.htmlFor = "sunnysign-link-target";
  targetLabel.textContent = t("editor.linkTarget");
  const targetInput = document.createElement("input");
  // type=text: type=url lehnt mailto:/tel: und Platzhalter in vielen Browsern ab.
  targetInput.type = "text";
  targetInput.id = "sunnysign-link-target";
  targetInput.className = "sunnysign-input sunnysign-editor-link-dialog__target";
  targetInput.value = initial.rest;
  targetInput.setAttribute("spellcheck", "false");
  targetInput.autocomplete = "off";
  targetField.appendChild(targetLabel);
  targetField.appendChild(targetInput);

  grid.appendChild(schemeField);
  grid.appendChild(targetField);

  const preview = document.createElement("p");
  preview.className = "sunnysign-editor-link-dialog__preview";

  // Enthält das Ziel Profilfelder, zeigt die Vorschau sie mit den Werten des angemeldeten Benutzers.
  const profileValueByToken = new Map(profileFields.map((field) => [field.token, (field.value ?? "").trim()]));
  const resolveProfileTokens = (href: string): string | null => {
    let resolved = href;
    let found = false;
    profileValueByToken.forEach((value, token) => {
      if (!resolved.includes(token)) return;
      found = true;
      resolved = resolved.split(token).join(value || t("editor.linkPreviewNoValue"));
    });
    return found ? resolved : null;
  };

  const errorEl = document.createElement("p");
  errorEl.className = "sunnysign-editor-link-error";
  errorEl.setAttribute("role", "alert");
  errorEl.hidden = true;

  const noStyleRow = document.createElement("div");
  noStyleRow.className = "sunnysign-editor-link-no-style-row";
  const noStyleCheckbox = document.createElement("input");
  noStyleCheckbox.type = "checkbox";
  noStyleCheckbox.id = "sunnysign-link-no-style";
  noStyleCheckbox.className = "sunnysign-editor-link-no-style-checkbox";
  noStyleCheckbox.checked = existingAnchor?.style.textDecoration === "none";
  const noStyleLabel = document.createElement("label");
  noStyleLabel.htmlFor = "sunnysign-link-no-style";
  noStyleLabel.className = "sunnysign-editor-link-no-style-label";
  noStyleLabel.textContent = t("editor.linkNoStyle");
  noStyleRow.appendChild(noStyleCheckbox);
  noStyleRow.appendChild(noStyleLabel);

  const actions = document.createElement("div");
  actions.className = "sunnysign-editor-color-dialog__actions";
  const cancelBtn = document.createElement("button");
  cancelBtn.type = "button";
  cancelBtn.className = "sunnysign-btn sunnysign-btn--secondary";
  cancelBtn.textContent = t("form.cancel");
  const okBtn = document.createElement("button");
  okBtn.type = "button";
  okBtn.className = "sunnysign-btn sunnysign-btn--primary";
  okBtn.textContent = t("editor.colorOk");
  actions.appendChild(cancelBtn);
  actions.appendChild(okBtn);

  const buildHref = (): string => {
    const scheme = schemeSelect.value;
    const target = targetInput.value.trim();
    // tel: verträgt keine Leerzeichen, Outlook wählt sonst nur den ersten Teil.
    return scheme + (scheme === "tel:" ? target.replace(/\s+/g, "") : target);
  };

  const clearLinkError = (): void => {
    errorEl.textContent = "";
    errorEl.hidden = true;
  };

  const showLinkError = (msg: string): void => {
    errorEl.textContent = msg;
    errorEl.hidden = false;
    showMessage?.(msg);
  };

  const syncPlaceholderAndPreview = (): void => {
    const scheme = LINK_SCHEMES.find((entry) => entry.value === schemeSelect.value) ?? LINK_SCHEMES[0];
    targetInput.placeholder = t(scheme.placeholderKey);
    const target = targetInput.value.trim();
    const href = target ? buildHref() : "";
    preview.textContent = target ? resolveProfileTokens(href) ?? href : t(scheme.placeholderKey);
    preview.classList.toggle("is-placeholder", !target);
  };

  /** Ein eingefügtes „https://…“ soll das Präfix umstellen statt es zu verdoppeln. */
  const adoptPastedScheme = (): void => {
    const value = targetInput.value;
    const lower = value.trim().toLowerCase();
    const pasted = LINK_SCHEMES.find((scheme) => lower.startsWith(scheme.value));
    if (!pasted) return;
    schemeSelect.value = pasted.value;
    targetInput.value = value.trim().slice(pasted.value.length);
  };

  box.appendChild(title);
  box.appendChild(grid);

  if (profileFields.length > 0) {
    // Auswahl und Erklärung stehen zusammen in einer Karte, damit die Zugehörigkeit sichtbar ist.
    const profileCard = document.createElement("div");
    profileCard.className = "sunnysign-editor-link-dialog__profile-card";
    const profileRow = document.createElement("div");
    profileRow.className = "sunnysign-editor-link-dialog__profile-row";
    const profileLabel = document.createElement("span");
    profileLabel.className = "sunnysign-editor-link-dialog__label";
    profileLabel.textContent = t("editor.profileFields");
    const profileDropdown = createSearchableDropdown({
      title: t("editor.linkProfileHint"),
      searchPlaceholder: t("editor.profileSearch"),
      extraClass: "sunnysign-editor-linkprofilelist",
      triggerLabel: t("editor.linkProfileInsert"),
      options: profileFields.map((field) => ({
        value: field.token,
        label: field.label,
        suffix: field.hasValue ? "•" : undefined,
        hint: profileFieldOptionHint(t, field),
      })),
      onPick: (token) => {
        const start = targetInput.selectionStart ?? targetInput.value.length;
        const end = targetInput.selectionEnd ?? start;
        targetInput.value = targetInput.value.slice(0, start) + token + targetInput.value.slice(end);
        const caret = start + token.length;
        targetInput.focus();
        targetInput.setSelectionRange(caret, caret);
        clearLinkError();
        syncPlaceholderAndPreview();
      },
    });
    profileRow.appendChild(profileLabel);
    profileRow.appendChild(profileDropdown.element);
    profileCard.appendChild(profileRow);

    const hint = document.createElement("p");
    hint.className = "sunnysign-editor-link-dialog__hint";
    hint.textContent = t("editor.linkProfileHint");
    profileCard.appendChild(hint);
    box.appendChild(profileCard);
  }

  box.appendChild(preview);
  box.appendChild(errorEl);
  box.appendChild(noStyleRow);
  box.appendChild(actions);
  overlay.appendChild(box);
  document.body.appendChild(overlay);

  const close = (): void => overlay.remove();

  schemeSelect.addEventListener("change", () => {
    clearLinkError();
    syncPlaceholderAndPreview();
    targetInput.focus();
  });
  targetInput.addEventListener("input", () => {
    adoptPastedScheme();
    clearLinkError();
    syncPlaceholderAndPreview();
  });
  targetInput.addEventListener("keydown", (event) => {
    if (event.key === "Enter") {
      event.preventDefault();
      okBtn.click();
    }
  });

  okBtn.addEventListener("click", () => {
    const target = targetInput.value.trim();
    if (!target) {
      showLinkError(t("editor.linkRequired"));
      targetInput.focus();
      return;
    }
    const href = buildHref();
    if (!isAllowedLinkHref(href)) {
      showLinkError(t("editor.linkBlocked"));
      targetInput.focus();
      return;
    }

    editorBody?.focus();
    if (editorRange) {
      const sel = window.getSelection();
      sel?.removeAllRanges();
      sel?.addRange(editorRange);
    }

    const collapsed = window.getSelection()?.isCollapsed ?? true;
    let anchor: HTMLAnchorElement | null = existingAnchor;
    if (!anchor && collapsed) {
      anchor = insertLinkAtCaret(href, target);
    } else if (!anchor) {
      exec("createLink", href);
      anchor = getLinkInSelection();
    }

    if (anchor) {
      // Platzhalter im Ziel dürfen nicht prozentkodiert werden – Attribut selbst setzen.
      anchor.setAttribute("href", href);
      if (noStyleCheckbox.checked) clearLinkStyle(anchor);
    }
    editorBody?.focus();
    close();
  });
  cancelBtn.addEventListener("click", close);
  overlay.addEventListener("click", (event) => {
    if (event.target === overlay) close();
  });

  syncPlaceholderAndPreview();
  targetInput.focus();
  targetInput.setSelectionRange(targetInput.value.length, targetInput.value.length);
}

function createToolbarButton(icon: string, title: string, onClick: () => void, command?: string): HTMLButtonElement {
  const btn = document.createElement("button");
  btn.type = "button";
  btn.className = "sunnysign-editor-btn";
  if (command) btn.setAttribute("data-command", command);
  btn.innerHTML = icon;
  btn.title = title;
  btn.addEventListener("click", (e) => {
    e.preventDefault();
    onClick();
  });
  return btn;
}

function createSelect(options: { label: string; value: string }[], title: string, onChange: (value: string) => void): HTMLSelectElement {
  const sel = document.createElement("select");
  sel.className = "sunnysign-editor-select";
  sel.title = title;
  options.forEach((o) => {
    const opt = document.createElement("option");
    opt.textContent = o.label;
    opt.value = o.value;
    sel.appendChild(opt);
  });
  sel.addEventListener("change", () => onChange(sel.value));
  return sel;
}

let editorBody: HTMLDivElement | null = null;
let savedSelectionRange: Range | null = null;

function saveSelection(): void {
  const sel = window.getSelection();
  if (sel && sel.rangeCount && editorBody && editorBody.contains(sel.anchorNode)) {
    savedSelectionRange = sel.getRangeAt(0).cloneRange();
  } else {
    savedSelectionRange = null;
  }
}

function restoreSelection(): boolean {
  if (!savedSelectionRange || !editorBody) return false;
  const sel = window.getSelection();
  if (!sel) return false;
  sel.removeAllRanges();
  sel.addRange(savedSelectionRange);
  savedSelectionRange = null;
  return true;
}

function exec(cmd: string, value?: string): void {
  document.execCommand(cmd, false, value ?? undefined);
  editorBody?.focus();
}

/** Erste Familie einer font-family-Liste normalisiert (für Vergleiche). */
function fontFamilyKey(fontFamily: string): string {
  const first = (fontFamily || "").split(",")[0] ?? "";
  return first.replace(/["']/g, "").trim().toLowerCase();
}

/**
 * Element, dessen Stil an der Cursorposition gilt. Liefert null, wenn die
 * Auswahl außerhalb des Editors liegt.
 */
function getCaretStyleElement(body: HTMLElement): HTMLElement | null {
  const sel = window.getSelection();
  if (!sel || sel.rangeCount === 0) return null;
  const node = sel.focusNode ?? sel.anchorNode;
  if (!node || !body.contains(node)) return null;
  if (node.nodeType === Node.ELEMENT_NODE) {
    const children = (node as Element).childNodes;
    const before = children[Math.min(sel.focusOffset, children.length) - 1];
    if (before && before.nodeType === Node.ELEMENT_NODE && (before as Element).tagName !== "BR") {
      return before as HTMLElement;
    }
    return node as HTMLElement;
  }
  const parent = node.parentElement;
  return parent && body.contains(parent) ? parent : null;
}

/**
 * Erste sichtbare Hintergrundfarbe zwischen Cursorposition und Editor-Rand.
 * explicit=false bedeutet: im Inhalt ist keine Hintergrundfarbe gesetzt.
 */
function resolveCaretBackground(el: HTMLElement, body: HTMLElement): { value: string; explicit: boolean } {
  let current: HTMLElement | null = el;
  while (current && current !== body) {
    const parsed = parseCssColor(window.getComputedStyle(current).backgroundColor);
    if (parsed && parsed.a > 0) return { value: cssColorToSwatchValue(parsed), explicit: true };
    current = current.parentElement;
  }
  const bodyBg = parseCssColor(window.getComputedStyle(body).backgroundColor);
  return { value: bodyBg && bodyBg.a > 0 ? cssColorToSwatchValue(bodyBg) : "#ffffff", explicit: false };
}

/** Anzeige, wenn eine Markierung mehrere unterschiedliche Werte enthält. */
const MIXED_DISPLAY = "—";
/** Interner Wert der Mixed-Option im Schriftart-Select (wird nie angewendet). */
const MIXED_VALUE = "__sunnysign-mixed__";

interface CaretFormat {
  fontFamily: string;
  fontSizePx: number;
  lineHeight: number;
  color: string;
  colorExplicit: boolean;
  background: string;
  backgroundExplicit: boolean;
}

const LINE_HEIGHT_BLOCKS = new Set(["DIV", "P", "LI", "TD", "TH", "BLOCKQUOTE"]);

function lineHeightBlockFor(element: HTMLElement, body: HTMLElement): HTMLElement | null {
  let current: HTMLElement | null = element;
  while (current && current !== body) {
    if (LINE_HEIGHT_BLOCKS.has(current.tagName)) return current;
    current = current.parentElement;
  }
  return null;
}

/** Effektiven Zeilenabstand als Faktor zur Schriftgröße lesen. */
function readLineHeightFactor(element: HTMLElement, body: HTMLElement): number {
  let current: HTMLElement | null = element;
  while (current && current !== body) {
    const raw = current.style.lineHeight.trim().toLowerCase();
    if (raw) {
      if (raw === "normal") return 1;
      if (/^\d+(?:\.\d+)?$/.test(raw)) return Number(raw);
      if (raw.endsWith("%")) return Number.parseFloat(raw) / 100;
      if (raw.endsWith("em")) return Number.parseFloat(raw);
      if (raw.endsWith("px")) {
        const fontSize = Number.parseFloat(window.getComputedStyle(current).fontSize);
        const lineHeight = Number.parseFloat(raw);
        if (fontSize > 0 && Number.isFinite(lineHeight)) return lineHeight / fontSize;
      }
    }
    current = current.parentElement;
  }

  const computed = window.getComputedStyle(element);
  if (computed.lineHeight === "normal") return 1;
  const fontSize = Number.parseFloat(computed.fontSize);
  const lineHeight = Number.parseFloat(computed.lineHeight);
  return fontSize > 0 && Number.isFinite(lineHeight) ? lineHeight / fontSize : 1;
}

/** Liest die an einer Stelle wirksame Formatierung (nur lesend). */
function readFormatAt(el: HTMLElement, body: HTMLElement, bodyColorHex: string): CaretFormat {
  const computed = window.getComputedStyle(el);
  const parsedColor = parseCssColor(computed.color);
  const colorHex = parsedColor ? rgbToHex(parsedColor.r, parsedColor.g, parsedColor.b) : bodyColorHex;
  const background = resolveCaretBackground(el, body);
  const sizePx = Math.round(parseFloat(computed.fontSize));
  return {
    fontFamily: computed.fontFamily,
    fontSizePx: Number.isFinite(sizePx) && sizePx > 0 ? sizePx : 0,
    lineHeight: Math.round(readLineHeightFactor(el, body) * 100) / 100,
    color: colorHex,
    colorExplicit: colorHex !== bodyColorHex,
    background: background.value,
    backgroundExplicit: background.explicit,
  };
}

/**
 * Elternelemente aller Textstellen innerhalb der Markierung, in Dokumentreihenfolge.
 * Stellen, die die Markierung nur berühren, zählen nicht mit.
 */
function collectSelectionStyleElements(range: Range, body: HTMLElement): HTMLElement[] {
  const container = range.commonAncestorContainer;
  const root = container.nodeType === Node.ELEMENT_NODE ? (container as HTMLElement) : container.parentElement;
  if (!root) return [];
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
  const nodeRange = document.createRange();
  const elements: HTMLElement[] = [];
  for (let node = walker.nextNode(); node; node = walker.nextNode()) {
    const parent = node.parentElement;
    if (!parent || !body.contains(parent)) continue;
    if (!(node.textContent ?? "").replace(/[\s\u200B]+/g, "")) continue;
    nodeRange.selectNodeContents(node);
    const endsAfterSelectionStart = nodeRange.compareBoundaryPoints(Range.START_TO_END, range) > 0;
    const startsBeforeSelectionEnd = nodeRange.compareBoundaryPoints(Range.END_TO_START, range) < 0;
    if (endsAfterSelectionStart && startsBeforeSelectionEnd) elements.push(parent);
  }
  return elements;
}

/** Wendet einen Zeilenabstand auf die aktuelle Zeile bzw. alle markierten Blöcke an. */
function applyLineHeight(body: HTMLElement, value: number): boolean {
  const selection = window.getSelection();
  if (!selection || selection.rangeCount === 0) return false;
  const range = selection.getRangeAt(0);
  if (!body.contains(range.commonAncestorContainer)) return false;

  const readSelectedElements = (): HTMLElement[] => {
    const currentSelection = window.getSelection();
    if (!currentSelection || currentSelection.rangeCount === 0) return [];
    const currentRange = currentSelection.getRangeAt(0);
    return currentRange.collapsed
      ? [getCaretStyleElement(body)].filter((element): element is HTMLElement => !!element)
      : collectSelectionStyleElements(currentRange, body);
  };
  const collectTargets = (elements: HTMLElement[]): HTMLElement[] =>
    Array.from(
      new Set(
        elements
          .map((element) => lineHeightBlockFor(element, body))
          .filter((element): element is HTMLElement => !!element)
      )
    );

  let styleElements = readSelectedElements();
  let targets = collectTargets(styleElements);

  // Direkter Text im contentEditable besitzt noch keinen Absatz. Nur bei einer
  // bewussten Änderung wird ein DIV erzeugt; reine Toolbar-Synchronisierung tut das nie.
  if (styleElements.some((element) => !lineHeightBlockFor(element, body))) {
    document.execCommand("formatBlock", false, "div");
    styleElements = readSelectedElements();
    targets = collectTargets(styleElements);
  }
  if (targets.length === 0) return false;

  const cssValue = String(Math.round(value * 100) / 100);
  targets.forEach((target) => {
    target.style.lineHeight = cssValue;
  });
  body.focus();
  return true;
}

/** Hex → HSL: h 0–360, s 0–100, l 0–100. */
function hexToHsl(hex: string): { h: number; s: number; l: number } {
  const { r, g, b } = hexToRgb(normalizeHex(hex));
  const rn = r / 255, gn = g / 255, bn = b / 255;
  const max = Math.max(rn, gn, bn), min = Math.min(rn, gn, bn);
  let h = 0, s = 0;
  const l = (max + min) / 2;
  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    if (max === rn) h = (gn - bn) / d + (gn < bn ? 6 : 0);
    else if (max === gn) h = (bn - rn) / d + 2;
    else h = (rn - gn) / d + 4;
    h /= 6;
  }
  return { h: h * 360, s: s * 100, l: l * 100 };
}

/** HSL → Hex. */
function hslToHex(h: number, s: number, l: number): string {
  s /= 100; l /= 100;
  const a = s * Math.min(l, 1 - l);
  const f = (n: number) => {
    const k = (n + h / 30) % 12;
    return l - a * Math.max(-1, Math.min(k - 3, 9 - k, 1));
  };
  return rgbToHex(f(0) * 255, f(8) * 255, f(4) * 255);
}

const PRESET_COLORS = [
  "#000000", "#ffffff", "#e41e1e", "#e91e63", "#9c27b0", "#673ab7", "#3f51b5",
  "#2196f3", "#00bcd4", "#009688", "#4caf50", "#8bc34a", "#cddc39", "#ffeb3b",
  "#ffc107", "#ff9800", "#ff5722", "#795548",
];

/** Parst initialen Farbwert: Hex oder rgba(r,g,b,a). */
function parseInitialColor(value: string): { hex: string; alpha: number } {
  const rgba = value.trim().match(/^rgba?\s*\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)\s*(?:,\s*([\d.]+)\s*)?\)$/);
  if (rgba) {
    const r = parseInt(rgba[1], 10), g = parseInt(rgba[2], 10), b = parseInt(rgba[3], 10);
    const alpha = rgba[4] != null ? parseFloat(rgba[4]) : 1;
    return { hex: rgbToHex(r, g, b), alpha: Math.max(0, Math.min(1, alpha)) };
  }
  return { hex: normalizeHex(value), alpha: 1 };
}

/** Öffnet Farb-Dialog: Swatches, eigene Farbscala (2D Punkt), Hex, bei Hintergrund + Transparenz-Schieber. */
function openColorDialog(
  titleKey: string,
  currentColor: string,
  t: (key: string) => string,
  onApply: (colorValue: string) => void,
  isBackground: boolean = false,
  /** false, wenn der Dialog aus einem anderen Dialog kommt und die Editor-Auswahl unangetastet bleiben muss. */
  syncEditorSelection: boolean = true
): void {
  const { hex: initialHex, alpha: initialAlpha } = parseInitialColor(currentColor);
  const overlay = document.createElement("div");
  overlay.className = "sunnysign-editor-dialog-overlay sunnysign-editor-color-dialog";
  overlay.setAttribute("role", "dialog");
  overlay.setAttribute("aria-modal", "true");
  const backdrop = document.createElement("div");
  backdrop.className = "sunnysign-editor-color-dialog__backdrop";
  const box = document.createElement("div");
  box.className = "sunnysign-editor-dialog-box sunnysign-editor-color-dialog__box";
  const title = document.createElement("p");
  title.className = "sunnysign-editor-color-dialog__title";
  title.textContent = t(titleKey);

  let selectedHex = initialHex;
  let selectedAlpha = initialAlpha;
  let hsl = hexToHsl(selectedHex);
  let alphaSlider: HTMLInputElement | null = null;
  let transparentBox: HTMLInputElement | null = null;

  // Nur bei voller Transparenz: eine neue Farbe sonst unsichtbar; Deckkraft auf 100 %.
  const revealIfFullyTransparent = (): void => {
    if (!isBackground || selectedAlpha !== 0) return;
    selectedAlpha = 1;
    if (alphaSlider) alphaSlider.value = "100";
    if (transparentBox) transparentBox.checked = false;
  };

  const updateHexFromInput = (v: string) => {
    if (isValidHex(v)) {
      selectedHex = normalizeHex(v);
      hsl = hexToHsl(selectedHex);
      revealIfFullyTransparent();
      updatePickerUI();
    }
  };
  const updatePickerUI = () => {
    hueSlider.style.left = `${(hsl.h / 360) * 100}%`;
    const slX = (hsl.s / 100) * 100;
    const slY = 100 - (hsl.l / 100) * 100;
    slPoint.style.left = `${slX}%`;
    slPoint.style.top = `${slY}%`;
    slBox.style.backgroundColor = hslToHex(hsl.h, 100, 50);
  };
  const setFromHsl = () => {
    selectedHex = hslToHex(hsl.h, hsl.s, hsl.l);
    hexInput.value = selectedHex;
    revealIfFullyTransparent();
  };

  const swatchGrid = document.createElement("div");
  swatchGrid.className = "sunnysign-editor-color-swatches";
  for (const hex of PRESET_COLORS) {
    const swatch = document.createElement("button");
    swatch.type = "button";
    swatch.className = "sunnysign-editor-color-swatch";
    swatch.style.backgroundColor = hex;
    swatch.title = hex;
    swatch.setAttribute("aria-label", hex);
    if (hex === "#ffffff") swatch.classList.add("sunnysign-editor-color-swatch--light");
    swatch.addEventListener("click", () => {
      selectedHex = hex;
      hsl = hexToHsl(hex);
      hexInput.value = hex;
      revealIfFullyTransparent();
      updatePickerUI();
    });
    swatchGrid.appendChild(swatch);
  }

  const scaleLabel = document.createElement("p");
  scaleLabel.className = "sunnysign-editor-color-scale-label";
  scaleLabel.textContent = "Farbton";
  const hueBar = document.createElement("div");
  hueBar.className = "sunnysign-editor-color-hue-bar";
  hueBar.setAttribute("aria-label", "Farbton");
  const hueSlider = document.createElement("div");
  hueSlider.className = "sunnysign-editor-color-hue-slider";
  hueBar.appendChild(hueSlider);
  const slLabel = document.createElement("p");
  slLabel.className = "sunnysign-editor-color-scale-label";
  slLabel.textContent = "Farbe wählen";
  const slBox = document.createElement("div");
  slBox.className = "sunnysign-editor-color-sl-box";
  slBox.style.backgroundColor = hslToHex(hsl.h, 100, 50);
  const slPoint = document.createElement("div");
  slPoint.className = "sunnysign-editor-color-sl-point";
  slBox.appendChild(slPoint);

  const getHueFromEvent = (e: MouseEvent | TouchEvent) => {
    const rect = hueBar.getBoundingClientRect();
    const clientX = "touches" in e ? e.touches[0].clientX : (e as MouseEvent).clientX;
    const pct = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width));
    return pct * 360;
  };
  const getSlFromEvent = (e: MouseEvent | TouchEvent) => {
    const rect = slBox.getBoundingClientRect();
    const clientX = "touches" in e ? e.touches[0].clientX : (e as MouseEvent).clientX;
    const clientY = "touches" in e ? e.touches[0].clientY : (e as MouseEvent).clientY;
    const s = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width)) * 100;
    const l = (1 - Math.max(0, Math.min(1, (clientY - rect.top) / rect.height))) * 100;
    return { s, l };
  };
  hueBar.addEventListener("mousedown", (e) => {
    e.preventDefault();
    hsl.h = getHueFromEvent(e);
    setFromHsl();
    updatePickerUI();
    const move = (e2: MouseEvent) => { hsl.h = getHueFromEvent(e2); setFromHsl(); updatePickerUI(); };
    const up = () => { document.removeEventListener("mousemove", move); document.removeEventListener("mouseup", up); };
    document.addEventListener("mousemove", move);
    document.addEventListener("mouseup", up);
  });
  slBox.addEventListener("mousedown", (e) => {
    e.preventDefault();
    const { s, l } = getSlFromEvent(e);
    hsl.s = s; hsl.l = l;
    setFromHsl();
    updatePickerUI();
    const move = (e2: MouseEvent) => {
      const sl = getSlFromEvent(e2);
      hsl.s = sl.s; hsl.l = sl.l;
      setFromHsl();
      updatePickerUI();
    };
    const up = () => { document.removeEventListener("mousemove", move); document.removeEventListener("mouseup", up); };
    document.addEventListener("mousemove", move);
    document.addEventListener("mouseup", up);
  });
  updatePickerUI();

  const hexLabel = document.createElement("label");
  hexLabel.className = "sunnysign-editor-color-hex-label";
  hexLabel.textContent = "Hex:";
  const hexInput = document.createElement("input");
  hexInput.type = "text";
  hexInput.className = "sunnysign-input sunnysign-editor-color-hex-input";
  hexInput.value = selectedHex;
  hexInput.setAttribute("aria-label", "Hex");
  hexInput.addEventListener("input", () => { updateHexFromInput(hexInput.value.trim()); });
  hexInput.addEventListener("change", () => {
    const v = hexInput.value.trim();
    if (isValidHex(v)) {
      selectedHex = normalizeHex(v);
      hexInput.value = selectedHex;
      hsl = hexToHsl(selectedHex);
      revealIfFullyTransparent();
      updatePickerUI();
    }
  });

  let alphaRow: HTMLDivElement | null = null;
  let alphaLabel: HTMLElement | null = null;
  let transparentRow: HTMLDivElement | null = null;
  if (isBackground) {
    alphaRow = document.createElement("div");
    alphaRow.className = "sunnysign-editor-color-alpha-row";
    alphaLabel = document.createElement("label");
    alphaLabel.className = "sunnysign-editor-color-hex-label";
    alphaLabel.textContent = t("editor.opacity");
    alphaSlider = document.createElement("input");
    alphaSlider.type = "range";
    alphaSlider.min = "0";
    alphaSlider.max = "100";
    alphaSlider.value = String(Math.round(initialAlpha * 100));
    alphaSlider.className = "sunnysign-editor-color-alpha-slider";
    alphaSlider.setAttribute("aria-label", t("editor.opacity"));
    alphaRow.appendChild(alphaLabel);
    alphaRow.appendChild(alphaSlider);

    // Abkürzung für die beiden Extremwerte: ganz transparent oder ganz deckend.
    transparentRow = document.createElement("div");
    transparentRow.className = "sunnysign-editor-color-transparent-row";
    transparentBox = document.createElement("input");
    transparentBox.type = "checkbox";
    transparentBox.id = "sunnysign-color-transparent";
    transparentBox.className = "sunnysign-editor-color-transparent-box";
    transparentBox.checked = initialAlpha === 0;
    const transparentLabel = document.createElement("label");
    transparentLabel.className = "sunnysign-editor-color-transparent-label";
    transparentLabel.setAttribute("for", transparentBox.id);
    transparentLabel.textContent = t("editor.colorTransparent");
    transparentRow.appendChild(transparentBox);
    transparentRow.appendChild(transparentLabel);

    alphaSlider.addEventListener("input", () => {
      selectedAlpha = alphaSlider!.valueAsNumber / 100;
      transparentBox!.checked = selectedAlpha === 0;
    });
    transparentBox.addEventListener("change", () => {
      selectedAlpha = transparentBox!.checked ? 0 : 1;
      alphaSlider!.value = String(Math.round(selectedAlpha * 100));
    });
  }

  const actions = document.createElement("div");
  actions.className = "sunnysign-editor-color-dialog__actions";
  const cancelBtn = document.createElement("button");
  cancelBtn.type = "button";
  cancelBtn.className = "sunnysign-btn sunnysign-btn--secondary";
  cancelBtn.textContent = t("form.cancel");
  const okBtn = document.createElement("button");
  okBtn.type = "button";
  okBtn.className = "sunnysign-btn sunnysign-btn--primary";
  okBtn.textContent = t("editor.colorOk");
  actions.appendChild(cancelBtn);
  actions.appendChild(okBtn);

  box.appendChild(title);
  box.appendChild(swatchGrid);
  box.appendChild(scaleLabel);
  box.appendChild(hueBar);
  box.appendChild(slLabel);
  box.appendChild(slBox);
  box.appendChild(hexLabel);
  box.appendChild(hexInput);
  if (alphaRow) box.appendChild(alphaRow);
  if (transparentRow) box.appendChild(transparentRow);
  box.appendChild(actions);
  overlay.appendChild(backdrop);
  overlay.appendChild(box);
  document.body.appendChild(overlay);

  const close = (): void => overlay.remove();
  okBtn.addEventListener("click", () => {
    const v = hexInput.value.trim();
    const hex = isValidHex(v) ? normalizeHex(v) : selectedHex;
    if (syncEditorSelection) restoreSelection();
    if (isBackground && alphaSlider && selectedAlpha < 1) {
      const { r, g, b } = hexToRgb(hex);
      onApply(`rgba(${r},${g},${b},${selectedAlpha})`);
    } else {
      onApply(hex);
    }
    if (syncEditorSelection) editorBody?.focus();
    close();
  });
  cancelBtn.addEventListener("click", close);
  backdrop.addEventListener("click", close);
  hexInput.focus();
}

/** Wendet Schriftfarbe an; bei leerer Auswahl wird ein Span mit Text- und ggf. Hintergrundfarbe eingefügt, damit der folgende Text beides erbt. */
function applyForeColor(hex: string, currentBgHex?: string): void {
  if (!editorBody) return;
  restoreSelection();
  const sel = window.getSelection();
  if (!sel || !sel.rangeCount) return;
  const range = sel.getRangeAt(0);
  const normalized = normalizeHex(hex);
  if (range.collapsed) {
    const span = document.createElement("span");
    span.style.color = normalized;
    if (currentBgHex) span.style.backgroundColor = /^rgba?/.test(currentBgHex.trim()) ? currentBgHex.trim() : normalizeHex(currentBgHex);
    span.appendChild(document.createTextNode("\u200B"));
    range.insertNode(span);
    range.setStart(span.firstChild!, 1);
    range.setEnd(span.firstChild!, 1);
    sel.removeAllRanges();
    sel.addRange(range);
  } else {
    document.execCommand("foreColor", false, normalized);
  }
  editorBody.focus();
}

/** Wendet Hintergrundfarbe an; colorValue ist Hex oder rgba(r,g,b,a). */
function applyBackColor(colorValue: string, currentTextHex?: string): void {
  if (!editorBody) return;
  restoreSelection();
  const sel = window.getSelection();
  if (!sel || !sel.rangeCount) return;
  const range = sel.getRangeAt(0);
  const bgValue = /^rgba?/.test(colorValue.trim()) ? colorValue.trim() : normalizeHex(colorValue);
  const useSpan = range.collapsed || /^rgba?/.test(colorValue.trim());
  if (useSpan) {
    const span = document.createElement("span");
    span.style.backgroundColor = bgValue;
    if (currentTextHex) span.style.color = normalizeHex(currentTextHex);
    if (range.collapsed) {
      span.appendChild(document.createTextNode("\u200B"));
      range.insertNode(span);
      range.setStart(span.firstChild!, 1);
      range.setEnd(span.firstChild!, 1);
    } else {
      span.appendChild(range.extractContents());
      range.insertNode(span);
    }
    sel.removeAllRanges();
    sel.addRange(range);
  } else {
    document.execCommand("backColor", false, bgValue);
  }
  editorBody.focus();
}

/** Setzt die Cursorposition ans Ende des Editor-Inhalts, damit insertHTML dort einfügt. */
function placeCaretAtEnd(container: HTMLElement): void {
  const sel = window.getSelection();
  if (!sel) return;
  const range = document.createRange();
  range.selectNodeContents(container);
  range.collapse(false);
  sel.removeAllRanges();
  sel.addRange(range);
}

function insertHtml(html: string): void {
  document.execCommand("insertHTML", false, html);
  editorBody?.focus();
}

/** Reiner Umbruch-Whitespace zählt beim Löschen nicht als Inhalt. */
function isBlankText(node: Node | null): boolean {
  return !!node && node.nodeType === Node.TEXT_NODE && !(node.textContent ?? "").trim();
}

/** Überspringt leere Textknoten in eine Richtung. */
function skipBlankSiblings(node: Node | null, direction: "previous" | "next"): Node | null {
  let current = node;
  while (isBlankText(current)) {
    current = direction === "previous" ? current!.previousSibling : current!.nextSibling;
  }
  return current;
}

/**
 * Nachbarknoten in der gewünschten Richtung. Steht der Cursor am Rand seines
 * Knotens, wird so lange nach oben gegangen, bis ein Nachbar existiert.
 */
function neighbourAcrossAncestors(
  start: Node,
  root: HTMLElement,
  direction: "previous" | "next"
): Node | null {
  let node: Node | null = start;
  while (node && node !== root) {
    const sibling = skipBlankSiblings(
      direction === "previous" ? node.previousSibling : node.nextSibling,
      direction
    );
    if (sibling) return sibling;
    node = node.parentNode;
  }
  return null;
}

/** Tabelle, falls der Knoten eine ist oder sie als einzigen Inhalt umschließt. */
function asTable(node: Node | null): HTMLTableElement | null {
  if (!node || node.nodeType !== Node.ELEMENT_NODE) return null;
  const el = node as Element;
  if (el.tagName === "TABLE") return el as HTMLTableElement;
  const content = Array.from(el.childNodes).filter((child) => !isBlankText(child));
  return content.length === 1 ? asTable(content[0]) : null;
}

/** Tabelle unmittelbar vor dem Cursor (für Backspace). */
function tableBeforeCaret(body: HTMLElement, range: Range): HTMLTableElement | null {
  const container = range.startContainer;
  if (container.nodeType === Node.TEXT_NODE) {
    if (range.startOffset > 0) return null;
  } else if (range.startOffset > 0) {
    return asTable(skipBlankSiblings(container.childNodes[range.startOffset - 1] ?? null, "previous"));
  }
  return asTable(neighbourAcrossAncestors(container, body, "previous"));
}

/** Tabelle unmittelbar hinter dem Cursor (für Entf). */
function tableAfterCaret(body: HTMLElement, range: Range): HTMLTableElement | null {
  const container = range.endContainer;
  if (container.nodeType === Node.TEXT_NODE) {
    if (range.endOffset < (container.textContent ?? "").length) return null;
  } else if (range.endOffset < container.childNodes.length) {
    return asTable(skipBlankSiblings(container.childNodes[range.endOffset] ?? null, "next"));
  }
  return asTable(neighbourAcrossAncestors(container, body, "next"));
}

/**
 * Fügt reinen Text an der Auswahl ein und behält dabei die Formatierung der
 * Fundstelle. Wichtig für Platzhalter: insertHTML hebt den Text aus einem
 * umgebenden <span> heraus, wodurch er die Schrift des Editors annimmt.
 */
function insertPlainText(text: string): void {
  if (!document.execCommand("insertText", false, text)) {
    insertTextViaRange(text);
    editorBody?.dispatchEvent(new Event("input", { bubbles: true }));
  }
  editorBody?.focus();
}

/** Ersatzweg, falls der Host insertText nicht ausführt. */
function insertTextViaRange(text: string): void {
  const sel = window.getSelection();
  if (!sel || sel.rangeCount === 0) return;
  const range = sel.getRangeAt(0);
  if (editorBody && !editorBody.contains(range.commonAncestorContainer)) return;
  range.deleteContents();
  const node = document.createTextNode(text);
  range.insertNode(node);
  const after = document.createRange();
  after.setStartAfter(node);
  after.collapse(true);
  sel.removeAllRanges();
  sel.addRange(after);
}

/** Max. Rohgröße für eingebettete Base64-Bilder (Office.js-/Body-Limits). */
const MAX_EMBEDDED_IMAGE_BYTES = 200 * 1024;
/** Max. Rohgröße für Blob-Attachments. */
const MAX_ATTACHMENT_IMAGE_BYTES = 2 * 1024 * 1024;

const ALLOWED_IMAGE_MIME = new Set(["image/png", "image/jpeg", "image/gif", "image/webp"]);

function isAllowedImageFile(file: File, formats?: string[]): boolean {
  const allowed = (formats?.length ? formats : ["png", "jpeg", "gif", "webp"]).map((f) =>
    f === "jpg" ? "jpeg" : f.toLowerCase()
  );
  const mimeOk =
    (file.type === "image/png" && allowed.includes("png")) ||
    (file.type === "image/jpeg" && allowed.includes("jpeg")) ||
    (file.type === "image/gif" && allowed.includes("gif")) ||
    (file.type === "image/webp" && allowed.includes("webp"));
  if (mimeOk) return true;
  const name = file.name.toLowerCase();
  if (/\.png$/i.test(name) && allowed.includes("png")) return true;
  if (/\.jpe?g$/i.test(name) && allowed.includes("jpeg")) return true;
  if (/\.gif$/i.test(name) && allowed.includes("gif")) return true;
  if (/\.webp$/i.test(name) && allowed.includes("webp")) return true;
  return ALLOWED_IMAGE_MIME.has(file.type) && !formats?.length;
}

/** Erlaubt http(s)-URLs und data:image/(png|jpeg|gif|webp);base64,… */
function isAllowedImageUrl(url: string): boolean {
  const trimmed = url.trim();
  if (!trimmed) return false;
  if (/^data:image\/(?:png|jpe?g|gif|webp);base64,/i.test(trimmed)) {
    const compact = trimmed.replace(/\s+/g, "");
    return /^data:image\/(?:png|jpe?g|gif|webp);base64,[A-Za-z0-9+/=]+$/i.test(compact);
  }
  try {
    const u = new URL(trimmed);
    return u.protocol === "http:" || u.protocol === "https:";
  } catch {
    return false;
  }
}

const EXT_BY_ASSET_CTYPE = new Map<string, string>([
  ["image/png", "png"],
  ["image/jpeg", "jpg"],
  ["image/gif", "gif"],
  ["image/webp", "webp"],
]);

/** Anzeige-src (blob:/data:) → cid:… für persistierte Attachment-Bilder. */
function normalizeAssetImageSrcs(html: string): string {
  if (!html || typeof document === "undefined") return html;
  const div = parseHtmlInert(html);
  div.querySelectorAll("img[data-sunnysign-asset]").forEach((img) => {
    let fileName = img.getAttribute("data-sunnysign-filename")?.trim();
    if (!fileName) {
      const assetId = img.getAttribute("data-sunnysign-asset")?.trim().replace(/-/g, "");
      const ext = EXT_BY_ASSET_CTYPE.get(img.getAttribute("data-sunnysign-ctype")?.trim() ?? "");
      if (!assetId || !ext) return;
      fileName = `sunnysign-${assetId}.${ext}`;
      img.setAttribute("data-sunnysign-filename", fileName);
    }
    img.setAttribute("src", `cid:${fileName}`);
  });
  return div.innerHTML;
}

const VISUAL_SPACE_CLASS = "sunnysign-visual-space";
const VISUAL_SPACE_NORMAL_CLASS = "sunnysign-visual-space--normal";
const VISUAL_SPACE_NBSP_CLASS = "sunnysign-visual-space--nbsp";
const VISUAL_SELECTION_MARKER_CLASS = "sunnysign-visual-selection-marker";
const FORMATTING_MARKS_CLASS = "is-show-formatting-marks";
const EMPTY_BLOCK_CLASS = "sunnysign-empty-block";
const BLOCK_SELECTOR = "p, div, li, blockquote";

function createVisualSpace(value: " " | "\u00a0"): HTMLSpanElement {
  const kind =
    value === "\u00a0" ? VISUAL_SPACE_NBSP_CLASS : VISUAL_SPACE_NORMAL_CLASS;
  const span = document.createElement("span");
  span.className = `${VISUAL_SPACE_CLASS} ${kind}`;
  span.contentEditable = "false";
  span.setAttribute("aria-hidden", "true");
  span.textContent = value;
  return span;
}

function visualSpaceHtml(value: " " | "\u00a0"): string {
  return createVisualSpace(value).outerHTML;
}

/** Entfernt ausschließlich unsere temporären Leerzeichen-Spans. */
function unwrapVisualSpaces(root: ParentNode): void {
  root.querySelectorAll<HTMLElement>(`.${VISUAL_SPACE_CLASS}`).forEach((span) => {
    const value = span.classList.contains(VISUAL_SPACE_NBSP_CLASS) ? "\u00a0" : " ";
    span.replaceWith(document.createTextNode(value));
  });
}

/**
 * Wickelt normale und geschützte Leerzeichen getrennt. Bereits markierte
 * Leerzeichen werden vom TreeWalker übersprungen, daher ist die Funktion
 * beliebig oft ohne Doppelverschachtelung aufrufbar.
 */
function wrapVisualSpaces(root: HTMLElement): void {
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, {
    acceptNode: (node) => {
      if (!/[ \u00a0]/.test(node.nodeValue ?? "")) return NodeFilter.FILTER_REJECT;
      const parent = node.parentElement;
      if (
        !parent ||
        parent.closest(
          `.${VISUAL_SPACE_CLASS}, .${VISUAL_SELECTION_MARKER_CLASS}, script, style, textarea`
        )
      ) {
        return NodeFilter.FILTER_REJECT;
      }
      return NodeFilter.FILTER_ACCEPT;
    },
  });
  const textNodes: Text[] = [];
  while (walker.nextNode()) textNodes.push(walker.currentNode as Text);

  textNodes.forEach((textNode) => {
    const value = textNode.nodeValue ?? "";
    const fragment = document.createDocumentFragment();
    let start = 0;
    for (let index = 0; index < value.length; index += 1) {
      const char = value[index];
      if (char !== " " && char !== "\u00a0") continue;
      if (index > start) fragment.appendChild(document.createTextNode(value.slice(start, index)));
      // Das Originalzeichen bestimmt weiterhin die Breite; CSS zeichnet nur den Punkt darüber.
      fragment.appendChild(createVisualSpace(char));
      start = index + 1;
    }
    if (start < value.length) fragment.appendChild(document.createTextNode(value.slice(start)));
    textNode.replaceWith(fragment);
  });
}

/**
 * Kennzeichnet leere Blöcke, deren Absatzzeichen sonst eine zweite Zeile erzwingt.
 * Ein Block mit Text oder eingebetteten Elementen (Bild, Tabelle) bleibt unmarkiert.
 */
function markEmptyBlocks(root: HTMLElement): void {
  root.querySelectorAll<HTMLElement>(BLOCK_SELECTOR).forEach((block) => {
    const onlyBreaks = Array.from(block.children).every((child) => child.tagName === "BR");
    const empty = onlyBreaks && !(block.textContent ?? "").trim();
    block.classList.toggle(EMPTY_BLOCK_CLASS, empty);
  });
}

/** Die Markierung ist reine Editor-Hilfe und darf nie im gespeicherten HTML landen. */
function clearEmptyBlockMarks(root: ParentNode): void {
  root.querySelectorAll<HTMLElement>(`.${EMPTY_BLOCK_CLASS}`).forEach((block) => {
    block.classList.remove(EMPTY_BLOCK_CLASS);
    if (!block.className.trim()) block.removeAttribute("class");
  });
}

/**
 * DOM-Umbauten zerstören sonst die aktuelle Range. Temporäre Marker halten
 * Start und Ende fest und werden direkt nach dem Umbau wieder entfernt.
 */
function mutateWithPreservedSelection(root: HTMLElement, mutate: () => void): void {
  const selection = window.getSelection();
  const source =
    selection && selection.rangeCount > 0 ? selection.getRangeAt(0) : null;
  const preserve =
    !!source && root.contains(source.startContainer) && root.contains(source.endContainer);
  if (!preserve || !source || !selection) {
    mutate();
    return;
  }

  const collapsed = source.collapsed;
  const startMarker = document.createElement("span");
  startMarker.className = VISUAL_SELECTION_MARKER_CLASS;
  startMarker.contentEditable = "false";
  const endMarker = collapsed ? null : document.createElement("span");
  if (endMarker) {
    endMarker.className = VISUAL_SELECTION_MARKER_CLASS;
    endMarker.contentEditable = "false";
    const endRange = source.cloneRange();
    endRange.collapse(false);
    endRange.insertNode(endMarker);
  }
  const startRange = source.cloneRange();
  startRange.collapse(true);
  startRange.insertNode(startMarker);

  mutate();

  const restored = document.createRange();
  if (collapsed) {
    restored.setStartBefore(startMarker);
    restored.collapse(true);
  } else if (endMarker) {
    restored.setStartAfter(startMarker);
    restored.setEndBefore(endMarker);
  }
  selection.removeAllRanges();
  selection.addRange(restored);
  endMarker?.remove();
  startMarker.remove();
}

/** Persistierbares HTML aus einer Kopie; der sichtbare Editor bleibt unangetastet. */
function editorHtmlWithoutVisualSpaces(body: Element): string {
  const clone = body.cloneNode(true) as HTMLElement;
  unwrapVisualSpaces(clone);
  clearEmptyBlockMarks(clone);
  clone.classList.remove(FORMATTING_MARKS_CLASS);
  clone.querySelectorAll(`.${VISUAL_SELECTION_MARKER_CLASS}`).forEach((marker) => marker.remove());
  return clone.innerHTML;
}

export function initSignatureEditor(
  wrapperId: string,
  t: (key: string) => string,
  profileFields?: ProfileFieldOption[],
  showMessage?: (msg: string) => void,
  options?: SignatureEditorInitOptions
): {
  getContent: () => string;
  setContent: (html: string) => void;
  /** True, wenn die letzte getContent-/setContent-Sanitisierung unerlaubte URIs entfernt hat. */
  didBlockDisallowedContent: () => boolean;
} {
  const wrapper = document.getElementById(wrapperId);
  if (!wrapper) {
    return { getContent: () => "", setContent: () => {}, didBlockDisallowedContent: () => false };
  }

  const showProfileFields = options?.showProfileFields !== false;
  const allowImageUrl = options?.allowImageUrl !== false;
  const allowImageBase64 = options?.allowImageBase64 === true;
  const allowImageAsAttachment = options?.allowImageAsAttachment === true;
  const allowedAttachmentFormats = options?.allowedAttachmentFormats;
  const onContentChange = options?.onContentChange;
  const showImageButton = allowImageUrl || allowImageBase64 || allowImageAsAttachment;
  const fields = profileFields?.length ? profileFields : DEFAULT_PROFILE_FIELDS;

  wrapper.innerHTML = "";
  // Der Wrapper überlebt den Formularwechsel: ohne dieses Zurücksetzen zeigt der
  // Editor beim nächsten Öffnen die leere Quelltextansicht der vorigen Sitzung.
  wrapper.classList.remove("is-html-source");
  const toolbar = document.createElement("div");
  toolbar.className = "sunnysign-editor-toolbar";

  const body = document.createElement("div");
  body.className = "sunnysign-editor-body";
  body.contentEditable = "true";
  body.setAttribute("data-placeholder", t(options?.placeholderKey ?? "form.placeholderContent"));
  editorBody = body;

  let formattingMarksActive = false;
  let formattingMarksButton: HTMLButtonElement | null = null;
  let formattingMarksRaf: number | null = null;
  let restoreFormattingMarksAfterSource = false;
  const formattingMarksObserver = new MutationObserver(() => {
    if (!body.isConnected) {
      formattingMarksObserver.disconnect();
      return;
    }
    scheduleFormattingMarks();
  });
  const observeFormattingMarks = (): void => {
    formattingMarksObserver.observe(body, {
      childList: true,
      characterData: true,
      subtree: true,
    });
  };
  const refreshFormattingMarks = (): void => {
    if (!formattingMarksActive || !body.isConnected) return;
    formattingMarksObserver.disconnect();
    mutateWithPreservedSelection(body, () => wrapVisualSpaces(body));
    markEmptyBlocks(body);
    formattingMarksObserver.takeRecords();
    observeFormattingMarks();
  };
  function scheduleFormattingMarks(): void {
    if (!formattingMarksActive || formattingMarksRaf !== null) return;
    formattingMarksRaf = requestAnimationFrame(() => {
      formattingMarksRaf = null;
      refreshFormattingMarks();
    });
  }
  const setFormattingMarks = (active: boolean): void => {
    if (formattingMarksRaf !== null) {
      cancelAnimationFrame(formattingMarksRaf);
      formattingMarksRaf = null;
    }
    formattingMarksObserver.disconnect();
    formattingMarksActive = active;
    body.classList.toggle(FORMATTING_MARKS_CLASS, active);
    if (active) {
      refreshFormattingMarks();
    } else {
      mutateWithPreservedSelection(body, () => unwrapVisualSpaces(body));
      clearEmptyBlockMarks(body);
    }
    if (formattingMarksButton) {
      formattingMarksButton.classList.toggle("sunnysign-editor-btn--active", active);
      formattingMarksButton.setAttribute("aria-pressed", active ? "true" : "false");
    }
  };
  // Der häufigste Fall (ein getipptes Leerzeichen) wird direkt als ein
  // execCommand-Schritt eingefügt. So bleibt er Teil des nativen Undo-Verlaufs,
  // statt erst nachträglich durch einen unabhängigen DOM-Umbau ersetzt zu werden.
  body.addEventListener("beforeinput", (event) => {
    if (
      !formattingMarksActive ||
      event.inputType !== "insertText" ||
      (event.data !== " " && event.data !== "\u00a0")
    ) {
      return;
    }
    event.preventDefault();
    let inserted = false;
    try {
      inserted = document.execCommand("insertHTML", false, visualSpaceHtml(event.data));
    } catch {
      inserted = false;
    }
    if (!inserted) {
      const selection = window.getSelection();
      const range =
        selection && selection.rangeCount > 0 ? selection.getRangeAt(0) : null;
      if (selection && range && body.contains(range.commonAncestorContainer)) {
        range.deleteContents();
        const span = createVisualSpace(event.data);
        range.insertNode(span);
        range.setStartAfter(span);
        range.collapse(true);
        selection.removeAllRanges();
        selection.addRange(range);
        onContentChange?.();
      }
    }
    scheduleFormattingMarks();
  });

  // Formatierzeile: Schrift, Betonung, Absatz, Einfügen. Die Gruppen brechen erst
  // um, wenn die Breite nicht reicht – bei breitem Fenster stehen sie in einer Zeile.
  const formatRow = document.createElement("div");
  formatRow.className = "sunnysign-editor-toolbar-row sunnysign-editor-format-row";
  /** Schriftgröße anwenden. Editor-Fokus und Auswahl müssen vorher wiederhergestellt sein. */
  const applyFontSize = (size: string) => {
    if (!editorBody || !size) return;
    const sel = window.getSelection();
    if (!sel || !sel.rangeCount) return;
    const range = sel.getRangeAt(0);
    if (!editorBody.contains(range.commonAncestorContainer)) return;
    const span = document.createElement("span");
    span.style.fontSize = size;
    if (range.collapsed) {
      span.appendChild(document.createTextNode("\u200B"));
      range.insertNode(span);
      range.setStart(span.firstChild!, 1);
      range.setEnd(span.firstChild!, 1);
    } else {
      span.appendChild(range.extractContents());
      range.insertNode(span);
    }
    sel.removeAllRanges();
    sel.addRange(range);
    editorBody.focus();
  };
  /** Schriftart anwenden (immer als span mit style="font-family: ...", damit Sanitizer und E-Mail die Schrift behalten). */
  const applyFontName = (fontValue: string) => {
    if (!editorBody) return;
    const sel = window.getSelection();
    if (!sel || !sel.rangeCount) return;
    const range = sel.getRangeAt(0);
    if (!editorBody.contains(range.commonAncestorContainer)) return;
    const font = fontValue || "Arial, Helvetica, sans-serif";
    const span = document.createElement("span");
    span.style.fontFamily = font;
    if (range.collapsed) {
      span.appendChild(document.createTextNode("\u200B"));
      range.insertNode(span);
      range.setStart(span.firstChild!, 1);
      range.setEnd(span.firstChild!, 1);
    } else {
      span.appendChild(range.extractContents());
      range.insertNode(span);
    }
    sel.removeAllRanges();
    sel.addRange(range);
    editorBody.focus();
  };
  const fontFamilyDropdown = createFontFamilyDropdown(t("editor.font"), t("editor.fontSearch"), (value) => {
    setTimeout(() => {
      editorBody?.focus();
      restoreSelection();
      applyFontName(value || "");
    }, 0);
  });
  const fontSizeDropdown = createFontSizeDropdown(
    t("editor.size"),
    t("editor.sizeCustom"),
    (v) => {
      setTimeout(() => {
        editorBody?.focus();
        restoreSelection();
        if (v) applyFontSize(v);
      }, 0);
    }
  );
  const lineHeightDropdown = createLineHeightDropdown(
    t("editor.lineHeight"),
    t("editor.lineHeightCustom"),
    t("editor.lineHeightInvalid"),
    (value) => {
      setTimeout(() => {
        editorBody?.focus();
        restoreSelection();
        if (editorBody && applyLineHeight(editorBody, value)) {
          onContentChange?.();
          updateToolbarState();
        }
      }, 0);
    }
  );
  /**
   * Farb-Swatch setzen. explicit=false: Farbe stammt aus dem Editor-Default, nicht aus dem Inhalt.
   * mixed=true: Markierung enthält mehrere Farben — angezeigt wird die des ersten Zeichens.
   */
  const setSwatch = (wrap: HTMLElement, value: string, explicit: boolean, mixed = false): void => {
    wrap.dataset.color = value;
    wrap.dataset.explicit = explicit ? "true" : "false";
    wrap.dataset.mixed = mixed ? "true" : "false";
    wrap.style.setProperty("--current-color", value);
  };
  const colorWrap = document.createElement("div");
  colorWrap.className = "sunnysign-editor-color-wrap";
  colorWrap.title = t("editor.color");
  setSwatch(colorWrap, "#000000", false);
  const colorIcon = document.createElement("span");
  colorIcon.className = "sunnysign-editor-color-icon";
  colorIcon.innerHTML = iconTextColor();
  colorWrap.appendChild(colorIcon);
  colorWrap.addEventListener("mousedown", (e) => { e.preventDefault(); saveSelection(); });
  colorWrap.addEventListener("click", (e) => {
    e.preventDefault();
    openColorDialog("editor.color", colorWrap.dataset.color || "#000000", t, (hex) => {
      applyForeColor(hex, bgWrap.dataset.explicit === "true" ? bgWrap.dataset.color : undefined);
      setSwatch(colorWrap, hex, true);
    });
  });
  const bgWrap = document.createElement("div");
  bgWrap.className = "sunnysign-editor-color-wrap";
  bgWrap.title = t("editor.bgColor");
  setSwatch(bgWrap, "#ffffff", false);
  const bgIcon = document.createElement("span");
  bgIcon.className = "sunnysign-editor-color-icon";
  bgIcon.innerHTML = iconBgColor();
  bgWrap.appendChild(bgIcon);
  bgWrap.addEventListener("mousedown", (e) => { e.preventDefault(); saveSelection(); });
  bgWrap.addEventListener("click", (e) => {
    e.preventDefault();
    openColorDialog("editor.bgColor", bgWrap.dataset.color || "#ffffff", t, (colorValue) => {
      applyBackColor(colorValue, colorWrap.dataset.explicit === "true" ? colorWrap.dataset.color : undefined);
      setSwatch(bgWrap, colorValue, true);
    }, true);
  });

  // Zeile 1 wie die Word-Gruppe „Schriftart“: Schrift, Auszeichnung, Farben.
  const fontGroup = document.createElement("div");
  fontGroup.className = "sunnysign-editor-toolbar-group";
  fontGroup.appendChild(fontFamilyDropdown.element);
  fontGroup.appendChild(fontSizeDropdown.element);
  // Auszeichnung und Farben; wird gefüllt, sobald updateToolbarState bereitsteht.
  const emphasisGroup = document.createElement("div");
  emphasisGroup.className = "sunnysign-editor-toolbar-group";
  formatRow.appendChild(fontGroup);
  formatRow.appendChild(emphasisGroup);
  toolbar.appendChild(formatRow);

  // Zusatzleiste für Tabellen; klappt auf, sobald der Cursor in einer Tabelle steht.
  const tableRow = document.createElement("div");
  tableRow.className = "sunnysign-editor-toolbar-row sunnysign-editor-table-row";
  tableRow.hidden = true;
  let tableContext: TableContext | null = null;
  let cellSelectionAnchor: HTMLTableCellElement | null = null;
  let cellSelection: TableCellSelection | null = null;
  let selectingCells = false;
  let syncMergeSplitButton: () => void = () => {};
  const SELECTED_CELL_CLASS = "sunnysign-cell-selected";

  const clearCellSelection = (): void => {
    cellSelection?.cells.forEach((cell) => {
      cell.classList.remove(SELECTED_CELL_CLASS);
      if (!cell.className.trim()) cell.removeAttribute("class");
    });
    cellSelection = null;
    syncMergeSplitButton();
  };

  const selectCellRectangle = (
    anchor: HTMLTableCellElement,
    focus: HTMLTableCellElement
  ): void => {
    clearCellSelection();
    const next = getTableCellSelection(anchor, focus);
    if (!next) return;
    cellSelection = next;
    next.cells.forEach((cell) => cell.classList.add(SELECTED_CELL_CLASS));
    syncMergeSplitButton();
  };

  const cellFromEvent = (event: Event): HTMLTableCellElement | null => {
    const target = event.target;
    if (!(target instanceof Element)) return null;
    const cell = target.closest("td,th") as HTMLTableCellElement | null;
    return cell && body.contains(cell) ? cell : null;
  };
  /**
   * Beim Ziehen einer Markierung wandert der Fokusknoten durch die Tabelle
   * hinein und hinaus; die Leiste würde dabei auf- und zuklappen. Sie wird
   * daher erst nach dem Loslassen der Maustaste neu bewertet.
   */
  let selectingWithMouse = false;
  /** Wird gesetzt, sobald der Profilfeld-Abschnitt aufgebaut ist. */
  let syncProfileButtons: () => void = () => {};

  /**
   * Übernimmt Schriftart, -größe, Farben und Button-Zustände aus der aktuellen
   * Cursorposition in die Toolbar. Liest nur, verändert den Inhalt nie.
   */
  const updateToolbarState = (): void => {
    if (!document.contains(body)) return;
    const sel = window.getSelection();
    const focusNode = sel && sel.rangeCount > 0 ? (sel.focusNode ?? sel.anchorNode) : null;
    const inEditor = !!(focusNode && body.contains(focusNode));
    toolbar.querySelectorAll<HTMLButtonElement>(".sunnysign-editor-btn[data-command]").forEach((btn) => {
      const cmd = btn.getAttribute("data-command");
      if (!cmd) return;
      let active = false;
      if (inEditor) {
        try {
          active = document.queryCommandState(cmd);
        } catch {
          active = false;
        }
      }
      btn.classList.toggle("sunnysign-editor-btn--active", active);
    });
    if (inEditor && !selectingWithMouse) {
      tableContext = findTableContext(body);
      tableRow.hidden = !tableContext;
      // Verschwindet die Leiste unter dem Mauszeiger, kommt kein mouseleave mehr.
      if (tableRow.hidden) clearScopePreview();
    }
    syncMergeSplitButton();
    syncProfileButtons();
    if (!inEditor) return;
    const bodyColor = parseCssColor(window.getComputedStyle(body).color);
    const bodyColorHex = bodyColor ? rgbToHex(bodyColor.r, bodyColor.g, bodyColor.b) : "#000000";

    // Bei einer Markierung zählt das erste Zeichen; abweichende Stellen ergeben "gemischt".
    const range = sel && sel.rangeCount > 0 ? sel.getRangeAt(0) : null;
    let elements = range && !range.collapsed ? collectSelectionStyleElements(range, body) : [];
    if (elements.length === 0) {
      const caretEl = getCaretStyleElement(body);
      if (!caretEl) return;
      elements = [caretEl];
    }
    const formats = elements.map((el) => readFormatAt(el, body, bodyColorHex));
    const first = formats[0];
    const uniform = <T>(pick: (f: CaretFormat) => T): boolean =>
      formats.every((f) => pick(f) === pick(first));
    const sameFamily = uniform((f) => fontFamilyKey(f.fontFamily));
    const sameSize = uniform((f) => f.fontSizePx);
    const sameLineHeight = uniform((f) => f.lineHeight);
    const sameColor = uniform((f) => f.color);
    const sameBackground = uniform((f) => f.background);
    const mixedHint = t("editor.mixedFormatting");

    if (!sameFamily) {
      fontFamilyDropdown.setDisplayValue(MIXED_DISPLAY, MIXED_VALUE);
      fontFamilyDropdown.element.title = `${t("editor.font")} · ${mixedHint}`;
    } else {
      fontFamilyDropdown.element.title = t("editor.font");
      const familyKey = fontFamilyKey(first.fontFamily);
      const match = FONT_OPTIONS.find((o) => o.value && fontFamilyKey(o.value) === familyKey);
      if (match) {
        fontFamilyDropdown.setDisplayValue(match.label, match.value);
      } else {
        // Schrift aus importiertem HTML: Name anzeigen, ohne Eintrag in der Liste.
        const label = (first.fontFamily.split(",")[0] ?? "").replace(/["']/g, "").trim();
        fontFamilyDropdown.setDisplayValue(label || FONT_OPTIONS[0].label, first.fontFamily);
      }
    }

    if (!sameSize) {
      fontSizeDropdown.setDisplayValue(MIXED_DISPLAY);
      fontSizeDropdown.element.title = `${t("editor.size")} · ${mixedHint}`;
    } else if (first.fontSizePx > 0) {
      fontSizeDropdown.setDisplayValue(`${first.fontSizePx}px`);
      fontSizeDropdown.element.title = t("editor.size");
    }

    if (!sameLineHeight) {
      lineHeightDropdown.setDisplayValue(MIXED_DISPLAY);
      lineHeightDropdown.element.title = `${t("editor.lineHeight")} · ${mixedHint}`;
    } else {
      lineHeightDropdown.setDisplayValue(formatLineHeight(first.lineHeight));
      lineHeightDropdown.element.title = t("editor.lineHeight");
    }

    setSwatch(colorWrap, first.color, sameColor && first.colorExplicit, !sameColor);
    colorWrap.title = sameColor ? t("editor.color") : `${t("editor.color")} · ${mixedHint}`;
    setSwatch(bgWrap, first.background, sameBackground && first.backgroundExplicit, !sameBackground);
    bgWrap.title = sameBackground ? t("editor.bgColor") : `${t("editor.bgColor")} · ${mixedHint}`;
  };

  let toolbarSyncRaf: number | null = null;
  const scheduleToolbarSync = (): void => {
    if (toolbarSyncRaf !== null) return;
    toolbarSyncRaf = requestAnimationFrame(() => {
      toolbarSyncRaf = null;
      updateToolbarState();
    });
  };
  // Editoren werden bei Ansichtswechsel neu aufgebaut: Listener am document wieder abmelden.
  const onDocumentSelectionChange = (): void => {
    if (!document.contains(body)) {
      document.removeEventListener("selectionchange", onDocumentSelectionChange);
      return;
    }
    scheduleToolbarSync();
  };
  document.addEventListener("selectionchange", onDocumentSelectionChange);
  body.addEventListener("keyup", scheduleToolbarSync);
  body.addEventListener("mouseup", scheduleToolbarSync);
  body.addEventListener("click", scheduleToolbarSync);
  body.addEventListener("focus", scheduleToolbarSync);
  body.addEventListener("input", scheduleToolbarSync);

  // Maustaste kann außerhalb des Editors losgelassen werden, daher am document.
  const onDocumentMouseUp = (): void => {
    if (!document.contains(body)) {
      document.removeEventListener("mouseup", onDocumentMouseUp);
      return;
    }
    selectingCells = false;
    if (!selectingWithMouse) return;
    selectingWithMouse = false;
    scheduleToolbarSync();
  };

  /**
   * Shift + Klick/Ziehen markiert ein Zellrechteck. Die native blaue
   * Textmarkierung wird dabei unterdrückt; ein normaler Mauszug markiert
   * weiterhin Text innerhalb der Zellen.
   */
  body.addEventListener(
    "mousedown",
    (event) => {
      const cell = cellFromEvent(event);
      if (!cell) {
        cellSelectionAnchor = null;
        clearCellSelection();
        return;
      }
      if (!event.shiftKey) {
        cellSelectionAnchor = cell;
        clearCellSelection();
        selectingWithMouse = true;
        return;
      }

      const table = cell.closest("table");
      const contextAnchor =
        tableContext?.cell.closest("table") === table ? tableContext.cell : null;
      const anchor =
        cellSelectionAnchor?.closest("table") === table
          ? cellSelectionAnchor
          : (contextAnchor ?? cell);
      event.preventDefault();
      event.stopPropagation();
      selectingWithMouse = false;
      selectingCells = true;
      cellSelectionAnchor = anchor;
      selectCellRectangle(anchor, cell);
      // Der Textcursor bleibt als Orientierung in der Ankerzelle.
      placeCaretInCell(anchor);
    },
    true
  );
  body.addEventListener("mouseover", (event) => {
    if (!selectingCells || !cellSelectionAnchor) return;
    const cell = cellFromEvent(event);
    if (!cell || cell.closest("table") !== cellSelectionAnchor.closest("table")) return;
    selectCellRectangle(cellSelectionAnchor, cell);
  });
  body.addEventListener(
    "click",
    (event) => {
      if (!event.shiftKey || !cellFromEvent(event)) return;
      event.preventDefault();
      event.stopPropagation();
    },
    true
  );
  document.addEventListener("mouseup", onDocumentMouseUp);

  /**
   * Entfernt eine Tabelle, die direkt neben dem Cursor steht. Ohne das bleibt
   * sie stehen, sobald hinter dem Cursor noch Inhalt folgt: Chromium löscht
   * dann lieber den Folgeabsatz als die Tabelle.
   */
  body.addEventListener("keydown", (e) => {
    if (e.key === "Escape" && cellSelection) {
      e.preventDefault();
      clearCellSelection();
      return;
    }
    if (e.key !== "Backspace" && e.key !== "Delete") return;
    const sel = window.getSelection();
    if (!sel || sel.rangeCount === 0 || !sel.isCollapsed) return;
    const range = sel.getRangeAt(0);
    if (!body.contains(range.startContainer)) return;
    // In einer Zelle gilt normales Löschen von Text.
    if (findTableContext(body)) return;
    const table = e.key === "Backspace" ? tableBeforeCaret(body, range) : tableAfterCaret(body, range);
    if (!table) return;
    e.preventDefault();
    table.remove();
    onContentChange?.();
    scheduleToolbarSync();
  });

  // Auszeichnung gehört zur Schriftart-Gruppe in Zeile 1.
  emphasisGroup.appendChild(createToolbarButton(iconBold(t), t("editor.bold"), () => { exec("bold"); updateToolbarState(); }, "bold"));
  emphasisGroup.appendChild(createToolbarButton(iconItalic(t), t("editor.italic"), () => { exec("italic"); updateToolbarState(); }, "italic"));
  const underlineBtn = createToolbarButton(iconUnderline(t), t("editor.underline"), () => {
    exec("underline");
    const a = getLinkInSelection();
    if (a) clearLinkStyle(a);
    updateToolbarState();
  }, "underline");
  underlineBtn.addEventListener("mousedown", () => saveSelection());
  emphasisGroup.appendChild(underlineBtn);
  emphasisGroup.appendChild(colorWrap);
  emphasisGroup.appendChild(bgWrap);

  // Word-Gruppe „Absatz“, dahinter die Einfügen-Schaltflächen.
  const paragraphGroup = document.createElement("div");
  paragraphGroup.className = "sunnysign-editor-toolbar-group";
  paragraphGroup.appendChild(createToolbarButton(iconAlignLeft(), t("editor.alignLeft"), () => { exec("justifyLeft"); updateToolbarState(); }, "justifyLeft"));
  paragraphGroup.appendChild(createToolbarButton(iconAlignCenter(), t("editor.alignCenter"), () => { exec("justifyCenter"); updateToolbarState(); }, "justifyCenter"));
  paragraphGroup.appendChild(createToolbarButton(iconAlignRight(), t("editor.alignRight"), () => { exec("justifyRight"); updateToolbarState(); }, "justifyRight"));
  paragraphGroup.appendChild(lineHeightDropdown.element);
  const tableDeps: TableEditorDeps = {
    t,
    // Aus dem Tabellen-Dialog heraus darf der Farbwähler die Editor-Auswahl nicht anfassen.
    pickColor: (titleKey, current, isBackground, onPick) =>
      openColorDialog(titleKey, current, t, onPick, isBackground, false),
    fontOptions: FONT_OPTIONS,
    notifyChange: () => {
      onContentChange?.();
      scheduleToolbarSync();
    },
  };
  /** Führt eine Tabellenaktion auf dem zuletzt erkannten Kontext aus (Toolbar-Klick behält die Cursorzelle). */
  const runTableAction = (action: (ctx: TableContext) => void) => (): void => {
    const ctx = tableContext;
    if (!ctx || !document.contains(ctx.cell)) return;
    clearCellSelection();
    action(ctx);
    cellSelectionAnchor = document.contains(ctx.cell) ? ctx.cell : null;
    if (!document.contains(ctx.table)) placeCaretAtEnd(body);
    body.focus();
    onContentChange?.();
    updateToolbarState();
  };

  const insertGroup = document.createElement("div");
  insertGroup.className = "sunnysign-editor-toolbar-group";
  const linkBtn = createToolbarButton(iconLink(), t("editor.link"), () =>
    openLinkDialog(t, showMessage, showProfileFields ? fields : [])
  );
  linkBtn.addEventListener("mousedown", () => saveSelection());
  insertGroup.appendChild(linkBtn);
  const hrBtn = createToolbarButton(iconHr(), t("editor.hr"), () => {
    openColorDialog("editor.hrColor", colorWrap.dataset.color || "#000000", t, (hex) => {
      body.focus();
      const sel = window.getSelection();
      const inEditor = !!(sel && sel.rangeCount > 0 && body.contains(sel.getRangeAt(0).commonAncestorContainer));
      if (!inEditor) placeCaretAtEnd(body);
      // Das color-Attribut zusätzlich, weil Outlook die Rahmenangabe an <hr> ignoriert.
      insertHtml(`<hr color="${hex}" style="border:none;border-top:1px solid ${hex};height:0">`);
      onContentChange?.();
      scheduleToolbarSync();
    });
  });
  hrBtn.addEventListener("mousedown", () => saveSelection());
  insertGroup.appendChild(hrBtn);
  if (showImageButton) {
    const imageBtn = createToolbarButton(iconImage(), t("editor.image"), () =>
      openImageDialog(body, t, showMessage, {
        allowImageUrl,
        allowImageBase64,
        allowImageAsAttachment,
        allowedAttachmentFormats,
      })
    );
    imageBtn.addEventListener("mousedown", () => saveSelection());
    insertGroup.appendChild(imageBtn);
  }
  const tableBtn = createToolbarButton(iconTable(), t("table.insert"), () => {
    openTableInsertDialog(tableDeps, (tableOptions) => {
      body.focus();
      if (!restoreSelection()) placeCaretAtEnd(body);
      const inserted = insertTableAtCaret(body, tableOptions);
      if (!inserted) {
        showMessage?.(t("table.insertFailed"));
        return;
      }
      body.focus();
      onContentChange?.();
      scheduleToolbarSync();
    });
  });
  tableBtn.addEventListener("mousedown", () => saveSelection());
  insertGroup.appendChild(tableBtn);
  formatRow.appendChild(paragraphGroup);
  formatRow.appendChild(insertGroup);

  // Tabellen-Zusatzleiste: Bereich formatieren + Zeilen/Spalten bearbeiten
  const tableCaption = document.createElement("span");
  tableCaption.className = "sunnysign-editor-table-row__caption";
  tableCaption.textContent = t("table.section");
  tableRow.appendChild(tableCaption);
  const scopeGroup = document.createElement("div");
  scopeGroup.className = "sunnysign-editor-toolbar-group";
  const scopeOptions: { scope: TableScope; label: string }[] = [
    { scope: "cell", label: t("table.scopeCell") },
    { scope: "row", label: t("table.scopeRow") },
    { scope: "column", label: t("table.scopeColumn") },
    { scope: "table", label: t("table.scopeTable") },
  ];
  scopeOptions.forEach(({ scope, label }) => {
    const btn = document.createElement("button");
    btn.type = "button";
    btn.className = "sunnysign-editor-btn sunnysign-editor-btn--text";
    btn.textContent = label;
    btn.title = `${t("table.formatTitle")}: ${label}`;
    btn.addEventListener("mousedown", (e) => e.preventDefault());
    btn.addEventListener("mouseenter", () => {
      const ctx = tableContext;
      if (!ctx || !document.contains(ctx.cell)) return;
      previewScopeCells(scope, ctx);
    });
    btn.addEventListener("mouseleave", () => clearScopePreview());
    btn.addEventListener("click", (e) => {
      e.preventDefault();
      const ctx = tableContext;
      if (!ctx || !document.contains(ctx.cell)) return;
      // Vor dem Dialog zurücknehmen, sonst liest er die Vorschaufarbe.
      clearScopePreview();
      openTableFormatDialog(tableDeps, scope, ctx);
    });
    scopeGroup.appendChild(btn);
  });
  const structureGroup = document.createElement("div");
  structureGroup.className = "sunnysign-editor-toolbar-group";
  const addStructureButton = (icon: string, titleKey: string, action: (ctx: TableContext) => void): void => {
    const btn = createToolbarButton(icon, t(titleKey), runTableAction(action));
    btn.addEventListener("mousedown", (e) => e.preventDefault());
    structureGroup.appendChild(btn);
  };
  addStructureButton(iconRowInsert("above"), "table.rowAbove", (ctx) => insertTableRow(ctx, "above"));
  addStructureButton(iconRowInsert("below"), "table.rowBelow", (ctx) => insertTableRow(ctx, "below"));
  addStructureButton(iconColumnInsert("left"), "table.columnLeft", (ctx) => insertTableColumn(ctx, "left"));
  addStructureButton(iconColumnInsert("right"), "table.columnRight", (ctx) => insertTableColumn(ctx, "right"));
  addStructureButton(iconRowDelete(), "table.deleteRow", (ctx) => deleteTableRow(ctx));
  addStructureButton(iconColumnDelete(), "table.deleteColumn", (ctx) => deleteTableColumn(ctx));

  let mergeSplitMode: "merge" | "split" | null = null;
  const mergeSplitBtn = createToolbarButton(iconMergeCells(), t("table.selectCellsHint"), () => {
    if (mergeSplitMode === "merge" && cellSelection) {
      const merged = mergeTableCells(cellSelection);
      clearCellSelection();
      if (!merged) return;
      cellSelectionAnchor = merged;
      tableContext = {
        table: merged.closest("table") as HTMLTableElement,
        row: merged.parentElement as HTMLTableRowElement,
        cell: merged,
      };
    } else if (mergeSplitMode === "split") {
      const cell =
        cellSelection?.cells.length === 1
          ? cellSelection.cells[0]
          : tableContext?.cell;
      if (!cell || splitTableCell(cell).length === 0) return;
      clearCellSelection();
      cellSelectionAnchor = cell;
      tableContext = {
        table: cell.closest("table") as HTMLTableElement,
        row: cell.parentElement as HTMLTableRowElement,
        cell,
      };
    } else {
      return;
    }
    body.focus();
    onContentChange?.();
    updateToolbarState();
  });
  mergeSplitBtn.disabled = true;
  mergeSplitBtn.addEventListener("mousedown", (event) => event.preventDefault());
  syncMergeSplitButton = (): void => {
    const mergeReady =
      !!cellSelection &&
      cellSelection.cells.length > 1 &&
      cellSelection.cells.every((cell) => document.contains(cell));
    const splitCandidate =
      cellSelection?.cells.length === 1
        ? cellSelection.cells[0]
        : tableContext?.cell;
    const splitReady =
      !mergeReady &&
      !!splitCandidate &&
      document.contains(splitCandidate) &&
      isMergedTableCell(splitCandidate);

    mergeSplitMode = mergeReady ? "merge" : splitReady ? "split" : null;
    mergeSplitBtn.disabled = mergeSplitMode === null;
    mergeSplitBtn.title =
      mergeSplitMode === "merge"
        ? t("table.mergeCells")
        : mergeSplitMode === "split"
          ? t("table.splitCells")
          : t("table.selectCellsHint");
    mergeSplitBtn.innerHTML = iconMergeCells(mergeSplitMode === "split");
    mergeSplitBtn.setAttribute("aria-label", mergeSplitBtn.title);
  };
  syncMergeSplitButton();
  structureGroup.appendChild(mergeSplitBtn);

  addStructureButton(iconTableDelete(), "table.deleteTable", (ctx) => deleteTable(ctx));
  tableRow.appendChild(scopeGroup);
  tableRow.appendChild(structureGroup);
  toolbar.appendChild(tableRow);

  // Profilfeld-Abschnitt: Feld einfügen und die beiden Ausblende-Markierungen
  if (showProfileFields) {
    const profileRow = document.createElement("div");
    profileRow.className = "sunnysign-editor-toolbar-row sunnysign-editor-profile-row";

    const profileDropdown = createSearchableDropdown({
      title: t("editor.profileFieldsHint"),
      searchPlaceholder: t("editor.profileSearch"),
      extraClass: "sunnysign-editor-profilelist",
      triggerLabel: t("editor.profileFields"),
      options: fields.map((f) => ({
        value: f.token,
        label: f.label,
        // Befüllte Felder erhalten einen Punkt hinter dem Namen.
        suffix: f.hasValue ? "•" : undefined,
        hint: profileFieldOptionHint(t, f),
      })),
      onPick: (token) => {
        body.focus();
        restoreSelection();
        insertPlainText(token);
        body.focus();
        scheduleToolbarSync();
      },
      onBeforeOpen: saveSelection,
    });
    profileRow.appendChild(profileDropdown.element);

    const hideGroup = document.createElement("div");
    hideGroup.className = "sunnysign-editor-toolbar-group";

    /** Aktionen wirken nur auf einen komplett markierten Platzhalter. */
    const addProfileAction = (
      label: string,
      hint: string,
      run: () => boolean
    ): HTMLButtonElement => {
      const btn = document.createElement("button");
      btn.type = "button";
      btn.className = "sunnysign-editor-btn sunnysign-editor-btn--text";
      const text = document.createElement("span");
      text.textContent = label;
      btn.appendChild(text);
      btn.setAttribute("aria-label", label);
      btn.addEventListener("mousedown", (e) => e.preventDefault());
      btn.addEventListener("click", () => {
        if (!run()) return;
        onContentChange?.();
        scheduleToolbarSync();
      });
      const wrap = createHintWrapper(hint);
      wrap.appendChild(btn);
      hideGroup.appendChild(wrap);
      return btn;
    };

    const hideLineBtn = addProfileAction(
      t("editor.hideLine"),
      t("editor.hideLineHint"),
      () => toggleHideLineAtCaret(body)
    );
    const hideFieldBtn = addProfileAction(
      t("editor.hideField"),
      t("editor.hideFieldHint"),
      () => toggleHideFieldAtCaret(body)
    );
    const deleteBtn = addProfileAction(
      t("editor.deletePlaceholder"),
      t("editor.deletePlaceholderHint"),
      () => deleteProfileSelection(body)
    );
    profileRow.appendChild(hideGroup);
    toolbar.appendChild(profileRow);

    syncProfileButtons = (): void => {
      // Markiert nebenbei angeklickte Platzhalter komplett (siehe normalizeProfileSelection).
      const state = selectingWithMouse ? { kind: "none" as const } : normalizeProfileSelection(body);
      const token = state.kind === "token" ? state.token : null;
      // Text mit genau einem Platzhalter lässt sich als Ganzes klammern.
      const span = state.kind === "span" ? state.span : null;
      hideLineBtn.disabled = token === null;
      hideFieldBtn.disabled = token === null && span === null;
      // Gelöscht wird nur als Ganzes: entweder das komplette `[# … #]` oder ein
      // Platzhalter ohne Klammerung.
      deleteBtn.disabled = state.kind !== "wrapper" && !(token && !token.hideFieldWrapped);
      // Ist keine der drei Aktionen möglich, entfällt die Zeile komplett und spart Platz.
      const anyEnabled = !hideLineBtn.disabled || !hideFieldBtn.disabled || !deleteBtn.disabled;
      hideGroup.style.display = anyEnabled ? "" : "none";
      hideLineBtn.classList.toggle("sunnysign-editor-btn--active", !!token?.parsed.hideLine);
      hideLineBtn.setAttribute("aria-pressed", token?.parsed.hideLine ? "true" : "false");
      const wrapped = !!token?.hideFieldWrapped || !!span?.hideFieldWrapped;
      hideFieldBtn.classList.toggle("sunnysign-editor-btn--active", wrapped);
      hideFieldBtn.setAttribute("aria-pressed", wrapped ? "true" : "false");
    };
    syncProfileButtons();
  }

  // Zeile 3: Rückgängig / Wiederherstellen links, HTML-Ansicht rechts
  const row3 = document.createElement("div");
  row3.className = "sunnysign-editor-toolbar-row sunnysign-editor-toolbar-row-undo";
  const groupUndo = document.createElement("div");
  groupUndo.className = "sunnysign-editor-toolbar-group";
  // Absatzmarken-Button vorerst nicht in der Leiste; die Hilfsanzeige bleibt im Code.
  groupUndo.appendChild(createToolbarButton(iconUndo(), t("editor.undo"), () => exec("undo")));
  groupUndo.appendChild(createToolbarButton(iconRedo(), t("editor.redo"), () => exec("redo")));
  row3.appendChild(groupUndo);

  const source = document.createElement("textarea");
  source.className = "sunnysign-editor-source";
  source.setAttribute("spellcheck", "false");
  source.setAttribute("aria-label", t("editor.htmlView"));

  let htmlSourceMode = false;
  const htmlSwitch = document.createElement("label");
  htmlSwitch.className = "sunnysign-editor-html-switch";
  htmlSwitch.title = t("editor.htmlViewTitle");
  const htmlSwitchText = document.createElement("span");
  htmlSwitchText.textContent = t("editor.htmlView");
  const htmlSwitchInput = document.createElement("input");
  htmlSwitchInput.type = "checkbox";
  htmlSwitchInput.setAttribute("aria-label", t("editor.htmlViewTitle"));
  const htmlSwitchTrack = document.createElement("span");
  htmlSwitchTrack.className = "sunnysign-editor-html-switch__track";
  htmlSwitchTrack.setAttribute("aria-hidden", "true");
  htmlSwitch.appendChild(htmlSwitchText);
  htmlSwitch.appendChild(htmlSwitchInput);
  htmlSwitch.appendChild(htmlSwitchTrack);
  row3.appendChild(htmlSwitch);
  toolbar.appendChild(row3);

  wrapper.appendChild(toolbar);
  wrapper.appendChild(body);
  wrapper.appendChild(source);

  let lastSanitizeBlocked = false;
  let notifiedSanitizeBlock = false;
  const applySanitize = (html: string, notifyUser: boolean): string => {
    const result = sanitizeSignatureHtmlResult(html);
    lastSanitizeBlocked = result.blocked;
    if (result.blocked && notifyUser && !notifiedSanitizeBlock) {
      notifiedSanitizeBlock = true;
      showMessage?.(t("editor.contentSanitized"));
    }
    if (!result.blocked) {
      notifiedSanitizeBlock = false;
    }
    return result.html;
  };

  const setHtmlSourceMode = (on: boolean): void => {
    if (on === htmlSourceMode) return;
    if (on) {
      clearScopePreview();
      clearCellSelection();
      restoreFormattingMarksAfterSource = formattingMarksActive;
      if (formattingMarksActive) setFormattingMarks(false);
      source.value = normalizeAssetImageSrcs(editorHtmlWithoutVisualSpaces(body));
      body.contentEditable = "false";
      wrapper.classList.add("is-html-source");
    } else {
      body.innerHTML = ensureImageDimensions(
        prepareAssetImagesForDisplay(applySanitize(source.value || "", true))
      );
      void resolveAssetImagesForDisplay(body);
      body.contentEditable = "true";
      wrapper.classList.remove("is-html-source");
      if (restoreFormattingMarksAfterSource) {
        restoreFormattingMarksAfterSource = false;
        setFormattingMarks(true);
      }
      onContentChange?.();
    }
    htmlSourceMode = on;
    htmlSwitchInput.checked = on;
  };
  htmlSwitchInput.addEventListener("change", () => {
    setHtmlSourceMode(htmlSwitchInput.checked);
  });

  if (onContentChange) {
    const notify = (): void => onContentChange();
    body.addEventListener("input", notify);
    body.addEventListener("paste", () => queueMicrotask(notify));
    source.addEventListener("input", notify);
  }

  return {
    getContent: () => {
      // Sicherheitsnetz: eine noch aktive Vorschau darf nie mitgespeichert werden.
      clearScopePreview();
      clearCellSelection();
      const raw = htmlSourceMode ? (source.value ?? "") : editorHtmlWithoutVisualSpaces(body);
      return applySanitize(normalizeAssetImageSrcs(raw), false);
    },
    setContent: (html: string) => {
      clearCellSelection();
      cellSelectionAnchor = null;
      const cleaned = ensureImageDimensions(
        prepareAssetImagesForDisplay(applySanitize(html || "", true))
      );
      if (htmlSourceMode) {
        source.value = normalizeAssetImageSrcs(cleaned);
      } else {
        body.innerHTML = cleaned;
        void resolveAssetImagesForDisplay(body);
        if (formattingMarksActive) refreshFormattingMarks();
      }
      onContentChange?.();
    },
    didBlockDisallowedContent: () => lastSanitizeBlocked,
  };
}

export function getSignatureEditorContent(): string {
  const wrap = document.querySelector(".sunnysign-signature-editor.is-html-source");
  if (wrap) {
    const source = wrap.querySelector(".sunnysign-editor-source") as HTMLTextAreaElement | null;
    if (source) return sanitizeSignatureHtml(normalizeAssetImageSrcs(source.value ?? ""));
  }
  const body = document.querySelector(".sunnysign-editor-body");
  body?.querySelectorAll(".sunnysign-cell-selected").forEach((cell) => {
    cell.classList.remove("sunnysign-cell-selected");
    if (!cell.className.trim()) cell.removeAttribute("class");
  });
  return body
    ? sanitizeSignatureHtml(normalizeAssetImageSrcs(editorHtmlWithoutVisualSpaces(body)))
    : "";
}

export function setSignatureEditorContent(html: string): void {
  const wrap = document.querySelector(".sunnysign-signature-editor.is-html-source");
  const cleaned = ensureImageDimensions(prepareAssetImagesForDisplay(sanitizeSignatureHtml(html || "")));
  if (wrap) {
    const source = wrap.querySelector(".sunnysign-editor-source") as HTMLTextAreaElement | null;
    if (source) {
      source.value = normalizeAssetImageSrcs(cleaned);
      return;
    }
  }
  const body = document.querySelector(".sunnysign-editor-body");
  if (!body) return;
  body.innerHTML = cleaned;
  void resolveAssetImagesForDisplay(body);
}
